/**
 * TealerLab — TEALER RUN web component (vanilla canvas).
 *
 * Gameplay (Jetpack Joyride style):
 *   - hold (click, tap, space, arrow up, W/Z) to thrust, release to fall
 *   - dodge the cops, collect pochons and stars, 3 lives
 *   - 5 zones: Paris → Berlin → Moscow → Tokyo → Overdrive (endless run)
 *
 * Economy and rewards are SERVER state (v2 contract). The base is relative — an
 * App Proxy path carried by `data-game-api-base` — so the storefront never issues
 * a cross-origin call:
 *   GET  {base}/state       → { customer, credits, bestScore, weekScore, dailyFree, rewardsToday }
 *   POST {base}/run/start   → { runId, credits } · 401 login_required · 402 no_credits
 *   POST {base}/run/finish  → { reward|null, credits, bestScore } — idempotent
 *   GET  {base}/leaderboard · GET {base}/winners — public
 *
 * The client never decides a reward, a credit balance or a score tier. Anything the
 * server does not answer (401/404/503/network/invalid JSON) drops the arena into DEMO
 * mode: playable, no credit spent, no reward displayed, discreet notice on screen.
 * localStorage only holds the demo best score and the mute preference.
 */

import { tlrEvents } from "@theme/tlr-events";

const STATE = {
  MENU: "menu",
  PLAYING: "playing",
  GAMEOVER: "gameover",
  NO_CREDITS: "no-credits",
};

// v3 : the server owns credits and scores — the v2 payload must not be resurrected.
const STORAGE_KEY = "tlr.game.v3";

// App Proxy path. Overridden by the theme setting through `data-game-api-base`.
const API_DEFAULT_BASE = "/apps/tealer-run";

// A player who has not touched the screen yet must not lose a life on the first
// free fall: invulnerability runs from the countdown to a bit after the GO.
const COUNTDOWN_GRACE_MS = 800;

// Asset slug per city index (Overdrive reuses the procedural renderer).
const CITY_ASSET_KEYS = ["paris", "berlin", "moscow", "hk"];

const CITIES = [
  { name: "Paris",     flag: "🇫🇷", sky: ["#6cb3e8", "#1e4d72"], building: "#d4a574", roof: "#7a4a2e", ground: "#3a2418" },
  { name: "Berlin",    flag: "🇩🇪", sky: ["#3a3a4a", "#0a0a14"], building: "#5a5a5a", roof: "#2a2a2a", ground: "#1a1a1a" },
  { name: "Moscou",    flag: "🇷🇺", sky: ["#d33a2c", "#5a1a14"], building: "#7c2d12", roof: "#3d1408", ground: "#2a0e08" },
  { name: "Tokyo",     flag: "🇯🇵", sky: ["#7a1f5a", "#100030"], building: "#1e1b4b", roof: "#0a0830", ground: "#050018" },
  // Overdrive — endless final run for the leaderboard
  { name: "Overdrive", flag: "🚧", sky: ["#ffd700", "#0a0a0a"], building: "#1a1a1a", roof: "#ffd700", ground: "#0a0a0a" },
];

const COP_COLORS = ["#1e3a8a", "#0a0a14", "#365314", "#4a0e6a", "#ffd700"]; // par city

const GRAVITY = 0.55;
const THRUST = -1.05;
const VY_MAX = 14;
const VY_MIN = -13;

// Hauteur logique FIXE du gameplay (px logiques). Le moteur tourne TOUJOURS dans
// ce référentiel, quelle que soit la taille réelle du canvas → la physique
// (gravité/poussée, en px/frame) ET les tailles d'entités restent cohérentes partout
// (mobile, desktop, fullscreen). La LARGEUR logique suit le ratio de l'écran : un
// runner horizontal montre juste un peu plus ou moins loin devant, le challenge
// vertical ne change pas. Le contexte 2D scale le rendu pour remplir le canvas réel.
const LOGICAL_GAME_H = 600;

// `| t` HTML-escapes its output and the strings land in textContent: decode here.
const ENTITIES = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"', "&#39;": "'" };

// A stalled App Proxy must not leave the arena waiting forever: an abort is read as a
// network failure, which the existing replay already handles.
const API_TIMEOUT_MS = 8000;

// 429 on start/finish: one replay, then the player is told to come back in a moment.
const RATE_LIMIT_RETRY_MS = 2000;

// Cart writes go through XMLHttpRequest, not window.fetch: a storefront cart app
// (SlideCart HQ on this store) monkey-patches window.fetch to sync its drawer, and
// its wrapper makes /cart/*.js calls hang forever. XHR is never patched. This timeout
// bounds a hung request so the reward-claim button never stays stuck on "applying".
const CART_TIMEOUT_MS = 10000;

// Media are awaited before a run opens, but never forever: a 404 or a stalled CDN must
// not lock the player out. The renderer already falls back to procedural drawing.
const PRELOAD_TIMEOUT_MS = 12000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** matchMedia is missing in a few embedded webviews: every caller must survive that. */
function mq(query) {
  return typeof window.matchMedia === "function" ? window.matchMedia(query) : null;
}
function mqMatches(query) {
  return !!mq(query)?.matches;
}
/** Safari < 14 only has the deprecated addListener/removeListener pair. */
function onMqChange(list, fn) {
  if (!list) return;
  if (typeof list.addEventListener === "function") list.addEventListener("change", fn);
  else if (typeof list.addListener === "function") list.addListener(fn);
}
function offMqChange(list, fn) {
  if (!list) return;
  if (typeof list.removeEventListener === "function") list.removeEventListener("change", fn);
  else if (typeof list.removeListener === "function") list.removeListener(fn);
}

class TlrGame extends HTMLElement {
  constructor() {
    super();
    this.state = STATE.MENU;
    this.i18n = {};
    this.score = 0;
    this.collectedCount = 0;
    this.lives = 3;
    this.distance = 0;
    this.cityIdx = 0;
    this.flag = "🇫🇷";
    this.player = null;
    this.cops = [];
    this.pochons = [];
    this.stars = [];
    this.smoke = [];
    this.bgOffset = 0;
    this.thrusting = false;
    this.boostUntil = 0;
    this.invincibleUntil = 0;
    this.gameStartTime = 0;
    this.lastFrameTime = 0;
    this.spawnGraceUntil = 0;
    this.spawnTimer = 0;
    this.starTimer = 8;
    this.gameLoop = null;
    this.canvas = null;
    this.ctx = null;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this._logicalScale = 1; // recalculé dans resizeCanvas (plafond résolution logique)
    this.inputBound = false;
    this.sprites = {};
    this.bgLayers = [];
    this._images = new Map();        // one <img> per URL: no asset is ever fetched twice
    this._cityAssetsLoaded = new Set();
    this._cityImages = [];
    this._missingAssets = new Set(); // grouped into ONE console warning
    this._missingAssetsTimer = 0;
    this._runToken = 0;              // invalidates a late answer from a previous run
    this._pausedAt = 0;              // portrait overlay: run frozen while > 0
    this._pendingStartToken = 0;     // portrait at click time: run armed, not opened
    this._portalling = false;        // true while the stage is moved in/out of <body>
    this._eventsBound = false;
    this._rotateBlocked = false;
    this._lifeSlots = 3;
    this.reducedMotion = false;
  }

  connectedCallback() {
    // The fullscreen portal moves the stage in and out of <body>. A move is a remove
    // plus an insert, and the browser fires disconnected/connected SYNCHRONOUSLY on
    // every custom element inside: without this flag a run in progress was reset
    // (token bumped, runId dropped, back to demo) in the middle of a game.
    if (this._portalling) return;
    this.i18n = this.readI18n();
    this.customerId = this.dataset.customerId || null;
    this.apiBase = (this.dataset.gameApiBase || API_DEFAULT_BASE).replace(/\/$/, "");
    // Theme editor: the App Proxy is not reachable from the preview, so the arena
    // stays in demo instead of showing an outage notice to the merchant.
    this.designMode = this.dataset.designMode === "true";

    this.session = this.loadSession();
    this.bestScore = this.session.bestScore || 0;
    this.credits = null;    // null = unknown (guest or demo): the HUD shows a dash
    this.demoMode = true;   // until /state proves a signed-in player
    this._demoOutage = false; // true when demo came from an outage, not a real guest
    this.rewardsOffline = false;
    this.runId = null;

    this._motionMq = mq("(prefers-reduced-motion: reduce)");
    this.reducedMotion = !!this._motionMq?.matches;
    this._onMotionChange = (e) => { this.reducedMotion = e.matches; };
    onMqChange(this._motionMq, this._onMotionChange);

    this.cacheRefs();
    this.bindEvents();
    this.applyState();
    this.updateBestScore();
    this.updateCreditsDisplay();
    this.initAudio();
    this.updateMuteButton();

    this._onResize = () => { if (this.canvas) this.resizeCanvas(); };
    window.addEventListener("resize", this._onResize);

    // The portrait overlay covers the canvas: watch it so a run never advances under it.
    this._portraitMq = mq("(orientation: portrait)");
    this._onPortraitChange = () => this.refreshRotateBlock();
    onMqChange(this._portraitMq, this._onPortraitChange);

    // Kept so a very early click on PLAY waits for the answer instead of assuming demo.
    this._statePromise = this.designMode ? null : this.loadServerState();
  }

  disconnectedCallback() {
    if (this._portalling) return; // see connectedCallback: the portal is not a teardown
    this._runToken += 1; // any in-flight run answer is stale from here
    if (this.gameLoop) cancelAnimationFrame(this.gameLoop);
    this.gameLoop = null;
    if (this.keyHandler) {
      window.removeEventListener("keydown", this.keyHandler);
      window.removeEventListener("keyup", this.keyHandler);
    }
    if (this._onResize) window.removeEventListener("resize", this._onResize);
    offMqChange(this._portraitMq, this._onPortraitChange);
    offMqChange(this._motionMq, this._onMotionChange);
    this.cancelUrlBarTimers();
    // A torn-down element keeps no watch on the live page: the theme editor re-renders
    // the section on every setting change, and an observer left behind would go on
    // hiding widgets with nobody to restore them.
    this.releaseFsWidgets();
  }

  /**
   * Stops watching for floating widgets and gives the page back: the widgets themselves,
   * the body lock and the black background. A half-done teardown is harder to diagnose
   * than no teardown — the page would stay locked with every culprit already restored.
   */
  releaseFsWidgets() {
    if (this._fsWidgetObserver) {
      this._fsWidgetObserver.disconnect();
      this._fsWidgetObserver = null;
    }
    if (Array.isArray(this._hiddenForFs)) {
      this._hiddenForFs.forEach(({ el, prevDisplay }) => { el.style.display = prevDisplay || ""; });
      this._hiddenForFs = [];
    }
    document.body.classList.remove("tlr-game-fs-active");
    document.body.style.minHeight = "";
    this.closest(".tlr-game-arena__stage")?.classList.remove("tlr-game-arena__stage--fullscreen-mobile");
  }

  /* ──────────────────────────────────────────
   * i18n — strings come from the section (data-game-i18n), never hardcoded here
   * ────────────────────────────────────────── */

  readI18n() {
    try {
      const raw = JSON.parse(this.dataset.gameI18n || "{}");
      for (const [key, value] of Object.entries(raw)) {
        if (typeof value === "string") raw[key] = value.replace(/&(amp|lt|gt|quot|#39);/g, (m) => ENTITIES[m]);
      }
      return raw;
    } catch {
      return {};
    }
  }

  t(key) {
    return this.i18n[key] || "";
  }

  /* ──────────────────────────────────────────
   * Local session — demo best score only.
   * Credits, scores and rewards are server state (v2 contract).
   * ────────────────────────────────────────── */

  loadSession() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return this.defaultSession();
      return { ...this.defaultSession(), ...JSON.parse(raw) };
    } catch {
      return this.defaultSession();
    }
  }

  defaultSession() {
    return { bestScore: 0, gamesPlayed: 0 };
  }

  saveSession() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.session)); } catch {}
  }

  /* ──────────────────────────────────────────
   * Server API (v2) — relative App Proxy base
   * ────────────────────────────────────────── */

  /** Single fetch entry point: never throws on an HTTP status nor on invalid JSON. */
  async apiFetch(path, body) {
    const init = {
      method: body === undefined ? "GET" : "POST",
      credentials: "same-origin",
      cache: "no-store",
    };
    if (body !== undefined) {
      init.headers = { "Content-Type": "application/json" };
      init.body = JSON.stringify(body);
    }
    if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
      init.signal = AbortSignal.timeout(API_TIMEOUT_MS);
    }
    const res = await fetch(`${this.apiBase}${path}`, init);
    let data = null;
    try { data = await res.json(); } catch { data = null; }
    return { ok: res.ok, status: res.status, data };
  }

  /**
   * Cart writes (/cart/add.js, /cart/update.js, /cart.js) via XMLHttpRequest, never
   * window.fetch: a cart app (SlideCart HQ) patches window.fetch and its wrapper makes
   * these calls hang, freezing the reward-claim button. XHR is not patched. Never throws:
   * a failure resolves to { ok:false } so the caller always continues.
   */
  cartRequest(path, { method = "GET", body = null } = {}) {
    return new Promise((resolve) => {
      let xhr;
      try { xhr = new XMLHttpRequest(); } catch { resolve({ ok: false, status: 0, data: null }); return; }
      xhr.open(method, path, true);
      xhr.timeout = CART_TIMEOUT_MS;
      xhr.withCredentials = true;
      xhr.setRequestHeader("Accept", "application/json");
      if (body != null) xhr.setRequestHeader("Content-Type", "application/json");
      xhr.onload = () => {
        let data = null;
        try { data = JSON.parse(xhr.responseText); } catch { data = null; }
        resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
      };
      xhr.onerror = () => resolve({ ok: false, status: 0, data: null });
      xhr.ontimeout = () => resolve({ ok: false, status: 0, data: null });
      xhr.send(body != null ? JSON.stringify(body) : null);
    });
  }

  /**
   * Demo = the arena stays playable, but no credit is spent and no reward is shown.
   * `outage` also raises the notice: a guest in demo is nominal, a dead API is not.
   */
  enterDemoMode({ outage = false } = {}) {
    this.demoMode = true;
    // outage = the server could not be reached or read; a fresh /state may still find a
    // signed-in player, so startGame() retries before opening a run. A real guest (401 /
    // customer:false) sets this false: nothing to retry, demo is the correct final state.
    this._demoOutage = outage;
    this.runId = null;
    this.credits = null;
    if (outage) this.markRewardsOffline();
    this.updateCreditsDisplay();
  }

  /** Raises the discreet "rewards unavailable" notice. Shown once, never removed. */
  markRewardsOffline() {
    if (this.rewardsOffline) return;
    this.rewardsOffline = true;
    this.refs?.notice?.removeAttribute("hidden");
  }

  /** Reads the server state once at load: signed-in or guest, credits, best score. */
  async loadServerState({ retry = true } = {}) {
    let res;
    try {
      res = await this.apiFetch("/state");
    } catch {
      // Network error or an aborted (slow) request: transient, not a verdict on the
      // player. Retry once; only a repeated failure falls back to demo, and even then
      // startGame() will try again before opening a run (see _demoOutage).
      if (retry) return this.loadServerState({ retry: false });
      this.enterDemoMode({ outage: true });
      return;
    }
    // 401 is the ONLY signal that latches demo for good: it means "not signed in".
    if (res.status === 401) {
      this.enterDemoMode();
      return;
    }
    const data = res.data;
    if (!res.ok || !data || typeof data !== "object") {
      // 5xx, or a 200 whose body could not be parsed (e.g. mangled by a fetch patch):
      // an intermediate state, never a reason to give up on the first try.
      if (retry) return this.loadServerState({ retry: false });
      this.enterDemoMode({ outage: true });
      return;
    }
    if (data.customer !== true) {
      this.enterDemoMode();
      return;
    }
    this.demoMode = false;
    this._demoOutage = false;
    if (Number.isFinite(data.credits)) this.credits = data.credits;
    if (Number.isFinite(data.bestScore) && data.bestScore > this.bestScore) {
      this.bestScore = data.bestScore;
    }
    this.updateCreditsDisplay();
    this.updateBestScore();
  }

  /**
   * Opens a run server-side. Returns "ok" | "no-credits" | "demo" | "stale".
   * "stale" = another run was triggered during the await — the caller drops this one
   * so a slow answer for run A never configures run B.
   */
  async startRun(token) {
    let rateLimitReplayed = false;
    for (;;) {
      let res;
      try {
        res = await this.apiFetch("/run/start", {});
      } catch {
        if (token !== this._runToken) return "stale";
        this.enterDemoMode({ outage: true });
        return "demo";
      }
      if (token !== this._runToken) return "stale";
      if (res.status === 429) {
        if (rateLimitReplayed) return "rate-limited";
        rateLimitReplayed = true;
        await wait(RATE_LIMIT_RETRY_MS);
        if (token !== this._runToken) return "stale";
        continue;
      }
      if (res.status === 401) { this.enterDemoMode(); return "demo"; }
      if (res.status === 402) {
        if (Number.isFinite(res.data?.credits)) this.credits = res.data.credits;
        this.updateCreditsDisplay();
        return "no-credits";
      }
      if (!res.ok || !res.data || res.data.runId == null) {
        this.enterDemoMode({ outage: true });
        return "demo";
      }
      this.runId = String(res.data.runId);
      if (Number.isFinite(res.data.credits)) this.credits = res.data.credits;
      this.updateCreditsDisplay();
      return "ok";
    }
  }

  /**
   * Closes a run server-side. The route is idempotent, so a replay is always safe:
   *   { status: "ok", data }      the server answered
   *   { status: "invalid" }       404 run_not_found / 422 implausible / other 4xx — final
   *   { status: "rateLimited" }   429 twice — final
   *   { status: "offline" }       network, timeout or 5xx after 3 attempts
   *   { status: "stale" }         a newer run took over during an await
   */
  async finishRun(payload, token) {
    let rateLimitReplayed = false;
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) await wait(500 * attempt);
      if (token !== this._runToken) return { status: "stale" };
      let res;
      try {
        res = await this.apiFetch("/run/finish", payload);
      } catch {
        continue; // network or timeout: the call may or may not have landed, replay it
      }
      if (token !== this._runToken) return { status: "stale" };
      if (res.ok && res.data && typeof res.data === "object") return { status: "ok", data: res.data };
      if (res.status === 429) {
        if (rateLimitReplayed) return { status: "rateLimited" };
        rateLimitReplayed = true;
        await wait(RATE_LIMIT_RETRY_MS);
        if (token !== this._runToken) return { status: "stale" };
        attempt -= 1; // the rate-limit replay does not consume a network attempt
        continue;
      }
      if (res.status >= 400 && res.status < 500) return { status: "invalid" };
    }
    return { status: "offline" };
  }

  /**
   * Media are fetched ON DEMAND, city by city — Paris when the player hits PLAY, the
   * next city as it comes into range. Loading the five cities up front pulled tens of
   * megabytes before any click, which no mobile LCP budget survives.
   *
   * Per city, in priority order:
   *   - N numbered plans (data-bg-{city}-plan-N) → real parallax, back to front
   *   - OR 3 layers (sky, clouds, city)
   *   - OR 1 full panorama
   *   - OR nothing → procedural renderer
   */
  buildCityLayers(key) {
    const cap = this.cap(key);

    // Parallax speeds, back (still) → front (fast), calibrated for ~3 foreground
    // repetitions over a 1500 m level.
    const speedsBySize = [
      [0.0],                                       // 1 plan : juste fond
      [0.0, 1.0],                                  // 2 plans
      [0.0, 0.3, 0.8],                             // 3 plans
      [0.0, 0.2, 0.5, 1.0],                        // 4 plans
      [0.0, 0.15, 0.35, 0.65, 1.05],               // 5 plans
      [0.0, 0.10, 0.25, 0.50, 0.80, 1.20],         // 6 plans
    ];
    const plans = [];
    // HTML5 dataset spec : data-foo-bar-1 → dataset['fooBar-1'] (le tiret devant un
    // chiffre reste). Convention : "plan 1" = premier plan (dessiné en dernier, le
    // plus rapide), "plan N" = arrière-plan (dessiné en premier, le plus lent).
    for (let i = 1; i <= 10; i++) {
      const url = this.dataset[`bg${cap}Plan-${i}`];
      if (!url) break;
      plans.push({ img: this.loadImage(url), speed: 0 });
    }
    if (plans.length > 0) {
      plans.reverse();
      const speeds = speedsBySize[plans.length - 1] || speedsBySize[speedsBySize.length - 1];
      plans.forEach((p, i) => { p.speed = speeds[i] ?? 0.5; });
      // draw() gives the plans absolute priority: the legacy layers below would be
      // downloaded for nothing, so they are not even requested.
      return { sky: null, clouds: null, city: null, full: null, plans };
    }

    return {
      sky:    this.loadImage(this.dataset[`bg${cap}Sky`]),          // sky = base, opaque
      clouds: this.loadImage(this.dataset[`bg${cap}Clouds`], true), // black → transparent
      city:   this.loadImage(this.dataset[`bg${cap}City`], true),   // black → transparent
      full:   this.loadImage(this.dataset[`bg${cap}`]),
      plans:  [],
    };
  }

  /**
   * Sprites shared by every city (player, collectibles). Loaded once, on PLAY.
   * Returns them every time, loaded or not: the caller waits on a DECLARED set, never on
   * "every image ever requested" — a previous run has already pulled the next cities.
   */
  loadSharedSprites() {
    if (!this._sharedSpritesLoaded) {
      this._sharedSpritesLoaded = true;
      this.sprites.tealerIdle   = this.loadImage(this.dataset.spriteTealerIdle);
      this.sprites.tealerThrust = this.loadImage(this.dataset.spriteTealerThrust);
      this.sprites.tealerBoost  = this.loadImage(this.dataset.spriteTealerBoost);
      this.sprites.pochon       = this.loadImage(this.dataset.spritePochon);
    }
    return [this.sprites.tealerIdle, this.sprites.tealerThrust,
            this.sprites.tealerBoost, this.sprites.pochon].filter(Boolean);
  }

  /**
   * Loads everything a city needs (backgrounds, cop and boss sprites), once.
   * Overdrive (index 4) has no artwork of its own: it renders procedurally.
   */
  ensureCityAssets(cityIdx) {
    const key = CITY_ASSET_KEYS[cityIdx];
    if (!key) return [];
    const cap = this.cap(key);
    if (!this._cityAssetsLoaded.has(cityIdx)) {
      this._cityAssetsLoaded.add(cityIdx);
      this.bgLayers[cityIdx] = this.buildCityLayers(key);
      this.sprites[`cop${cap}`]  = this.loadImage(this.dataset[`spriteCop${cap}`]);
      this.sprites[`boss${cap}`] = this.loadImage(this.dataset[`spriteBoss${cap}`]);
      // Same contract as loadSharedSprites: what this city needs, said out loud — and
      // built ONCE. tick() calls this every frame over the last 20 % of a city.
      const layers = this.bgLayers[cityIdx] || {};
      this._cityImages[cityIdx] = [layers.sky, layers.clouds, layers.city, layers.full,
        ...(layers.plans || []).map((plan) => plan.img),
        this.sprites[`cop${cap}`], this.sprites[`boss${cap}`]].filter(Boolean);
    }
    return this._cityImages[cityIdx] || [];
  }

  /**
   * Waits for the media this run needs, so the countdown never starts on a half-drawn
   * stage. The engine draws whatever has arrived: on a cold cache the run used to open
   * on an empty sky, then the player popped in, then the city. Bounded — a missing or
   * slow file must not hold the game hostage.
   */
  async preloadRunAssets(images, timeoutMs = PRELOAD_TIMEOUT_MS) {
    const pending = (images || []).filter((img) => img && !img.complete);
    if (!pending.length) return;
    let done = 0;
    const total = pending.length;
    this.setLoadingProgress(0, total);
    const all = Promise.all(pending.map((img) => this.whenImageSettled(img).then(() => {
      done += 1;
      this.setLoadingProgress(done, total);
    })));
    // Jamais indéfiniment : un fichier manquant ou lent ne doit pas bloquer le joueur
    // (le moteur sait dessiner sans ses médias). Plus de bouton « Jouer quand même » —
    // le chargement est rapide depuis que les médias sont hébergés, et le timeout est le
    // seul filet nécessaire.
    let timer;
    await Promise.race([
      all,
      new Promise((resolve) => { timer = setTimeout(resolve, timeoutMs); }),
    ]);
    clearTimeout(timer);
  }

  /**
   * Resolves when an image has finished loading, failed, or was already done.
   * A listener, never `onload`: loadImage's own `onload` does the colour-keying, and
   * assigning over it would draw the un-keyed sprite — a black box around the player.
   */
  whenImageSettled(img) {
    return new Promise((resolve) => {
      if (!img || img.complete) { resolve(); return; }
      const done = () => {
        img.removeEventListener("load", done);
        img.removeEventListener("error", done);
        resolve();
      };
      img.addEventListener("load", done);
      img.addEventListener("error", done);
    });
  }

  /** Covers the stage while a run is being prepared. Nothing else is clickable then. */
  setLoading(on) {
    const el = this.refs?.loading;
    if (!el) return;
    if (on) el.removeAttribute("hidden");
    else el.setAttribute("hidden", "");
  }

  setLoadingProgress(done, total) {
    const pct = total > 0 ? Math.round((done / total) * 100) : 100;
    if (this.refs?.loadingBar) this.refs.loadingBar.style.width = `${pct}%`;
    if (this.refs?.loadingCount) this.refs.loadingCount.textContent = `${done} / ${total}`;
  }

  /**
   * Initialise le système audio :
   *  - WebAudio pour les SFX synthétisés (pochon, damage, etc.)
   *  - <audio> HTML5 pour la musique de fond (MP3 boucle)
   * Les 2 sont initialisés en lazy (premier tap user) pour respecter
   * la politique autoplay des browsers.
   */
  initAudio() {
    this.audioCtx = null;
    this.musicGain = null;
    this.musicPlaying = false;
    this.muted = localStorage.getItem("tlr.game.muted") === "1";

    // 🎵 Musique de fond : fichier MP3 fourni via data-music (boucle).
    // Si absent / échec de décodage → fallback sur le synth WebAudio (_startMusicSynth).
    this.musicSrc = this.dataset.music || "";
    this.musicBuffer = null;        // AudioBuffer décodé (une fois)
    this.musicBufferSource = null;  // source en cours de lecture
    this._musicLoading = false;
    this._musicFailed = false;
    this._musicLevel = 0.15;        // niveau de croisière du bus (référence duck/stop)

    const ensureCtx = () => {
      if (!this.audioCtx) {
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          this.audioCtx = new AC();
          this.unlockAudio(); // iOS : débloque le contexte avec un buffer muet DANS le geste
        } catch (e) { /* pas d'audio */ }
      }
      // iOS/Android : le contexte naît souvent "suspended" (ou "interrupted" sur iOS).
      // On relance à CHAQUE geste tant qu'il n'est pas "running" (un seul resume ne suffit pas).
      if (this.audioCtx && this.audioCtx.state !== "running") this.audioCtx.resume().catch(() => {});
      // The MP3 is only fetched once a run is live: a gesture anywhere on the page
      // used to pull 3 MB of music from a visitor who never opened the game.
      if (this.state === STATE.PLAYING) this.loadMusicBuffer();
      // Autoplay souvent bloqué au startGame → on (re)lance la musique au 1er geste user.
      if (this.state === STATE.PLAYING && !this.muted && !this.musicPlaying) this.startMusic();
    };
    document.addEventListener("pointerdown", ensureCtx, { once: false });
    document.addEventListener("keydown", ensureCtx, { once: false });

    // Onglet caché / app en arrière-plan : on SUSPEND le contexte (pause propre, réversible)
    // au lieu de détruire le séquenceur. Sur mobile, le passage en fullscreen ou un blur bref
    // déclenchait sinon stopMusic() → audio coupé en plein jeu sans repartir. La reprise se
    // fait ici quand l'onglet redevient visible ET à chaque geste via ensureCtx.
    document.addEventListener("visibilitychange", () => {
      if (!this.audioCtx) return;
      if (document.hidden) {
        this.audioCtx.suspend().catch(() => {});
      } else {
        this.audioCtx.resume().catch(() => {});
        if (this.state === STATE.PLAYING && !this.muted && !this.musicPlaying) this.startMusic();
      }
    });
  }

  /** iOS : joue un buffer muet d'1 sample DANS le geste user pour débloquer l'AudioContext. */
  unlockAudio() {
    try {
      const buf = this.audioCtx.createBuffer(1, 1, 22050);
      const src = this.audioCtx.createBufferSource();
      src.buffer = buf;
      src.connect(this.audioCtx.destination);
      src.start(0);
    } catch (_) { /* no-op */ }
  }

  /**
   * Charge + décode le MP3 de fond (data-music) une seule fois dans un AudioBuffer.
   * Décodé via la forme callback de decodeAudioData (compatible Safari ancien).
   * En cas d'échec → _musicFailed = true → startMusic bascule sur le synth.
   */
  loadMusicBuffer() {
    if (!this.musicSrc || this.musicBuffer || this._musicLoading || this._musicFailed) return;
    if (!this.audioCtx) return;
    this._musicLoading = true;
    fetch(this.musicSrc)
      .then((r) => r.arrayBuffer())
      .then((ab) => new Promise((resolve, reject) => {
        this.audioCtx.decodeAudioData(ab, resolve, reject);
      }))
      .then((buf) => {
        this.musicBuffer = buf;
        this._musicLoading = false;
        // Si le jeu tournait en attendant le décodage → démarre la musique maintenant.
        if (this.state === STATE.PLAYING && !this.muted && !this.musicPlaying) this.startMusic();
      })
      .catch(() => {
        this._musicLoading = false;
        this._musicFailed = true; // → fallback synth
        if (this.state === STATE.PLAYING && !this.muted && !this.musicPlaying) this.startMusic();
      });
  }

  /**
   * Démarre la musique de fond. Priorité au fichier MP3 fourni (data-music) ;
   * fallback sur le synth WebAudio (Am–F–C–G) si pas de fichier ou décodage KO.
   * Les deux passent par le même bus this.musicGain → mute/duck/stop unifiés.
   */
  startMusic() {
    if (this.muted) return;
    if (!this.audioCtx) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        this.audioCtx = new AC();
      } catch (e) { return; }
    }
    if (this.audioCtx.state === "suspended") this.audioCtx.resume().catch(() => {});
    if (this.musicPlaying) return;

    if (!this.musicGain) {
      this.musicGain = this.audioCtx.createGain();
      this.musicGain.connect(this.audioCtx.destination);
    }

    // 🎵 MP3 fourni et pas en échec → on le joue en boucle.
    if (this.musicSrc && !this._musicFailed) {
      if (this.musicBuffer) { this._startMusicMp3(); return; }
      this.loadMusicBuffer(); // décode puis rappellera startMusic() une fois prêt
      return;                 // pas de synth en doublon : on attend le MP3 (préchargé au 1er geste)
    }
    this._startMusicSynth();
  }

  /** Lecture en boucle du MP3 de fond via le bus musicGain (fade-in doux). */
  _startMusicMp3() {
    this.musicPlaying = true;
    this._musicLevel = 0.42; // niveau de croisière (le MP3 est déjà à pleine amplitude)
    const t0 = this.audioCtx.currentTime;
    this.musicGain.gain.cancelScheduledValues(t0);
    this.musicGain.gain.setValueAtTime(0.0001, t0);
    this.musicGain.gain.exponentialRampToValueAtTime(this._musicLevel, t0 + 1.0);

    const src = this.audioCtx.createBufferSource();
    src.buffer = this.musicBuffer;
    src.loop = true;
    src.connect(this.musicGain);
    src.start(0);
    this.musicBufferSource = src;
  }

  /** Démarre la musique de fond SYNTHÉTISÉE (fallback : Am–F–C–G chiptune). */
  _startMusicSynth() {
    this.musicPlaying = true;
    this._musicLevel = 0.15;
    const t0 = this.audioCtx.currentTime;
    this.musicGain.gain.cancelScheduledValues(t0);
    this.musicGain.gain.setValueAtTime(0.0001, t0);
    this.musicGain.gain.exponentialRampToValueAtTime(this._musicLevel, t0 + 1.2); // fade-in doux

    this._musicStep = 0;
    this._musicNextTime = t0 + 0.12;
    clearInterval(this._musicTimer);
    this._musicTimer = setInterval(() => this.musicScheduler(), 60);
  }

  /** Séquenceur lookahead : programme les notes ~0.25s en avance (anti-glitch). */
  musicScheduler() {
    if (!this.audioCtx || !this.musicPlaying) return;
    const stepDur = 60 / 84 / 4; // double-croche @ 84 BPM
    while (this._musicNextTime < this.audioCtx.currentTime + 0.25) {
      this.musicPlayStep(this._musicStep, this._musicNextTime, stepDur);
      this._musicNextTime += stepDur;
      this._musicStep = (this._musicStep + 1) % 32; // 2 mesures (4 accords × 8 steps)
    }
  }

  /** Joue les notes d'un pas de la séquence (basse + arpège). */
  musicPlayStep(step, time, stepDur) {
    // Am – F – C – G (vi-IV-I-V), 8 steps par accord
    const chords = [
      { bass: 110.00, notes: [220.00, 261.63, 329.63] }, // Am
      { bass: 87.31,  notes: [174.61, 220.00, 261.63] }, // F
      { bass: 130.81, notes: [196.00, 261.63, 329.63] }, // C
      { bass: 98.00,  notes: [196.00, 246.94, 293.66] }, // G
    ];
    const chord = chords[Math.floor(step / 8) % 4];
    if (step % 8 === 0) this.musicNote(chord.bass, time, stepDur * 6, "triangle", 0.5);
    if (step % 2 === 0) {
      const note = chord.notes[((step / 2) | 0) % chord.notes.length];
      this.musicNote(note, time, stepDur * 1.7, "sine", 0.2);
    }
  }

  /** Une note du bus musique avec enveloppe douce. */
  musicNote(freq, time, dur, type, vol) {
    const ctx = this.audioCtx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, time);
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(vol, time + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    osc.connect(g);
    g.connect(this.musicGain);
    osc.start(time);
    osc.stop(time + dur + 0.05);
  }

  /** Stoppe la musique (game over / menu / onglet caché). Fade-out puis arrêt de la source MP3. */
  stopMusic() {
    this.musicPlaying = false;
    clearInterval(this._musicTimer);
    this._musicTimer = null;
    const now = this.audioCtx ? this.audioCtx.currentTime : 0;
    if (this.musicGain && this.audioCtx) {
      this.musicGain.gain.cancelScheduledValues(now);
      this.musicGain.gain.setValueAtTime(Math.max(0.0001, this.musicGain.gain.value), now);
      this.musicGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
    }
    if (this.musicBufferSource) {
      const src = this.musicBufferSource;
      this.musicBufferSource = null;
      try { src.onended = () => { try { src.disconnect(); } catch (_) {} }; } catch (_) {}
      try { src.stop(now + 0.45); } catch (_) { try { src.stop(); } catch (e) {} } // après le fade
    }
  }

  /** Ducking : baisse temporairement le volume musique pendant un SFX important. */
  duckMusic(durationMs = 200) {
    if (!this.musicGain || !this.audioCtx || this.muted || !this.musicPlaying) return;
    const now = this.audioCtx.currentTime;
    const level = this._musicLevel || 0.15;
    this.musicGain.gain.cancelScheduledValues(now);
    this.musicGain.gain.setValueAtTime(Math.min(0.04, level * 0.3), now);
    this.musicGain.gain.exponentialRampToValueAtTime(level, now + durationMs / 1000 + 0.1);
  }

  /**
   * Joue un SFX synthétisé.
   * preset = 'pochon' | 'star' | 'damage' | 'powerup' | 'heart' | 'gameover' | 'thrust' | 'boss' | 'achievement'
   */
  playSfx(preset, vol = 0.18) {
    if (this.muted || !this.audioCtx) return;
    const ctx = this.audioCtx;
    const t = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(vol, t);
    let dur = 0.1;
    switch (preset) {
      case "pochon":
        osc.type = "sine";
        osc.frequency.setValueAtTime(800, t);
        osc.frequency.exponentialRampToValueAtTime(1400, t + 0.08);
        dur = 0.1;
        break;
      case "star":
        osc.type = "triangle";
        osc.frequency.setValueAtTime(600, t);
        osc.frequency.exponentialRampToValueAtTime(1800, t + 0.15);
        dur = 0.2;
        break;
      case "damage":
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(180, t);
        osc.frequency.exponentialRampToValueAtTime(60, t + 0.25);
        dur = 0.3;
        vol = 0.28;
        gain.gain.setValueAtTime(vol, t);
        break;
      case "powerup":
        // Arpège montant net (G4→C5→G5) : clairement identifiable comme "power-up get".
        osc.type = "square";
        osc.frequency.setValueAtTime(392, t);
        osc.frequency.setValueAtTime(523, t + 0.06);
        osc.frequency.setValueAtTime(784, t + 0.12);
        dur = 0.26;
        vol = 0.3;
        gain.gain.setValueAtTime(vol, t);
        break;
      case "heart":
        // Triade majeure montante (C5→E5→G5) : petit jingle "soin / +1 vie".
        osc.type = "triangle";
        osc.frequency.setValueAtTime(523, t);
        osc.frequency.setValueAtTime(659, t + 0.07);
        osc.frequency.setValueAtTime(784, t + 0.14);
        dur = 0.28;
        vol = 0.26;
        gain.gain.setValueAtTime(vol, t);
        break;
      case "gameover":
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(440, t);
        osc.frequency.exponentialRampToValueAtTime(110, t + 0.7);
        dur = 0.8;
        vol = 0.3;
        gain.gain.setValueAtTime(vol, t);
        break;
      case "achievement":
        osc.type = "triangle";
        osc.frequency.setValueAtTime(700, t);
        osc.frequency.exponentialRampToValueAtTime(1500, t + 0.1);
        osc.frequency.exponentialRampToValueAtTime(2200, t + 0.2);
        dur = 0.3;
        break;
      case "boss":
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(80, t);
        osc.frequency.exponentialRampToValueAtTime(150, t + 0.4);
        dur = 0.5;
        vol = 0.3;
        gain.gain.setValueAtTime(vol, t);
        break;
      default:
        osc.frequency.setValueAtTime(440, t);
    }
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.start(t);
    osc.stop(t + dur + 0.05);
  }

  toggleMute() {
    this.muted = !this.muted;
    localStorage.setItem("tlr.game.muted", this.muted ? "1" : "0");
    if (this.muted) {
      this.stopMusic();
    } else {
      this.playSfx("pochon");
      if (this.state === STATE.PLAYING) this.startMusic();
    }
    this.updateMuteButton();
  }

  /** Mute button: icon, pressed state and label all follow this.muted. */
  updateMuteButton() {
    const btn = this.refs?.mute || this.querySelector("[data-tlr-mute]");
    if (!btn) return;
    btn.textContent = this.muted ? "🔇" : "🔊";
    btn.setAttribute("aria-pressed", this.muted ? "true" : "false");
    const label = this.t(this.muted ? "muteOn" : "muteOff");
    if (label) btn.setAttribute("aria-label", label);
  }

  /** Renvoie un sprite chargé OU un fallback si l'image n'est pas encore prête. */
  getSprite(name, fallback = null) {
    const s = this.sprites?.[name];
    if (s && s.complete && s.naturalWidth > 0) return s;
    if (fallback) return this.getSprite(fallback);
    return null;
  }

  cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  /**
   * Loads an image ONCE per URL — backgrounds and sprites share this cache, so a
   * sprite claimed by two cities is fetched a single time.
   *
   * keyOut=true and no native alpha (opaque PNG / JPEG) → the background colour
   * (sampled on the four corners) becomes transparent; otherwise the PNG alpha wins.
   *
   * No cache-busting query: `asset_url` already carries a version hash, and a
   * `Date.now()` suffix turned every call into a distinct URL — hence a second fetch.
   */
  loadImage(url, keyOut = false) {
    if (!url) return null;
    const cached = this._images.get(url);
    if (cached) return cached;
    const img = new Image();
    this._images.set(url, img);
    img.crossOrigin = "anonymous";
    const tag = url.split("/").pop()?.split("?")[0] || "?";
    img.onload = () => {
      if (!keyOut) return;
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        const cx = c.getContext("2d");
        cx.drawImage(img, 0, 0);
        const data = cx.getImageData(0, 0, c.width, c.height);
        const px = data.data;

        // Vérifie si l'image a déjà de l'alpha natif (PNG transparent)
        // Sample plusieurs coins : si un seul est < 250 alpha → on respecte l'alpha existant
        const sampleAlpha = [
          px[3],
          px[(c.width - 1) * 4 + 3],
          px[(c.width * (c.height - 1)) * 4 + 3],
          px[(c.width * c.height - 1) * 4 + 3],
        ];
        const hasNativeAlpha = sampleAlpha.some((a) => a < 250);
        if (hasNativeAlpha) {
          if (window.tlr?.debug) console.log(`[TEALER RUN] ${tag} : alpha PNG natif détecté, pas de keying`);
          return;
        }

        // Pas d'alpha natif → color-key le fond depuis les coins
        const cornerOf = (x, y) => {
          const i = (y * c.width + x) * 4;
          return [px[i], px[i + 1], px[i + 2]];
        };
        const corners = [
          cornerOf(0, 0),
          cornerOf(c.width - 1, 0),
          cornerOf(0, c.height - 1),
          cornerOf(c.width - 1, c.height - 1),
        ];
        const bg = [0, 0, 0];
        corners.forEach((cor) => { bg[0] += cor[0]; bg[1] += cor[1]; bg[2] += cor[2]; });
        bg[0] /= 4; bg[1] /= 4; bg[2] /= 4;

        const T1 = 35;
        const T2 = 80;
        for (let i = 0; i < px.length; i += 4) {
          const dr = px[i] - bg[0];
          const dg = px[i + 1] - bg[1];
          const db = px[i + 2] - bg[2];
          const dist = Math.sqrt(dr * dr + dg * dg + db * db);
          if (dist < T1) px[i + 3] = 0;
          else if (dist < T2) px[i + 3] = Math.floor(((dist - T1) / (T2 - T1)) * 255);
        }
        cx.putImageData(data, 0, 0);
        img._processed = c;
        if (window.tlr?.debug) console.log(`[TEALER RUN] ${tag} : color-keyed bg rgb(${bg[0]|0},${bg[1]|0},${bg[2]|0})`);
      } catch (e) {
        console.error(`[TEALER RUN] ${tag} keyOut FAILED:`, e.message);
      }
    };
    img.onerror = () => this.reportMissingAsset(tag);
    img.src = url;
    return img;
  }

  /**
   * 30 game media were dropped from the theme to stay under the 50 MB limit and still
   * 404. The renderer falls back to procedural drawing; one grouped warning replaces
   * the per-file flood the console used to get.
   */
  reportMissingAsset(tag) {
    this._missingAssets.add(tag);
    clearTimeout(this._missingAssetsTimer);
    this._missingAssetsTimer = setTimeout(() => {
      console.warn(`[TEALER RUN] ${this._missingAssets.size} media unavailable, procedural fallback used:`,
        Array.from(this._missingAssets).join(", "));
    }, 1500);
  }

  /* ──────────────────────────────────────────
   * DOM refs + events
   * ────────────────────────────────────────── */

  cacheRefs() {
    // Scoped to the section: the customizer can render the arena more than once.
    const arena = this.closest(".tlr-game-arena") || document;
    this.refs = {
      menu: this.querySelector("[data-tlr-menu]"),
      gameover: this.querySelector("[data-tlr-gameover]"),
      noCredits: this.querySelector("[data-tlr-no-credits]"),
      hud: this.querySelector(".tlr-game__hud"),
      score: this.querySelector("[data-tlr-score]"),
      lives: this.querySelector("[data-tlr-lives]"),
      distance: this.querySelector("[data-tlr-distance]"),
      flag: this.querySelector("[data-tlr-flag]"),
      cityName: this.querySelector("[data-tlr-cityname]"),
      combo: this.querySelector("[data-tlr-combo]"),
      comboWrap: this.querySelector("[data-tlr-combo-wrap]"),
      finalScore: this.querySelector("[data-tlr-final-score]"),
      bestScore: this.querySelector("[data-tlr-best-score]"),
      rewardsList: this.querySelector("[data-tlr-rewards-list]"),
      mute: this.querySelector("[data-tlr-mute]"),
      creditsValue: arena.querySelector("[data-tlr-credits-value]"),
      notice: arena.querySelector("[data-tlr-game-notice]"),
      rotatePrompt: arena.querySelector(".tlr-game-arena__rotate-prompt"),
      loading: arena.querySelector("[data-tlr-loading]"),
      loadingBar: arena.querySelector("[data-tlr-loading-bar]"),
      loadingCount: arena.querySelector("[data-tlr-loading-count]"),
    };
  }

  bindEvents() {
    if (this._eventsBound) return; // the portal used to stack one click listener per move
    this._eventsBound = true;
    this.addEventListener("click", (e) => {
      // Bouton mute (priorité haute, intercepte avant les autres handlers)
      if (e.target.closest("[data-tlr-mute]")) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMute();
        return;
      }
      // No-credits pack card: add to cart and go straight to checkout, no product detour.
      const nopack = e.target.closest("[data-tlr-nopack]");
      if (nopack) {
        e.preventDefault();
        this.buyPackAndCheckout(nopack).catch((err) => console.warn("[TEALER RUN] pack buy failed", err));
        return;
      }
      const action = e.target.closest("[data-tlr-action]")?.dataset.tlrAction;
      if (!action) return;
      e.preventDefault();
      // startGame is async: a dropped rejection would kill the click path in silence.
      if (action === "play" || action === "replay") {
        this.startGame().catch((err) => console.warn("[TEALER RUN] startGame failed", err));
      }
      if (action === "leave")  this.exitFullscreenMobile();
      if (action === "share")  this.shareScore();
      if (action === "buy")    this.openCheckout();
    });

    // Bouton "+" à côté du compteur crédits → scroll vers la section packs (3 cards)
    document.querySelector("[data-tlr-buy-credits]")?.addEventListener("click", () => {
      tlrEvents.emit("tlr_game_buy_intent", { source: "credits_plus_btn" });
      const target = document.querySelector("[data-tlr-game-cta], .tlr-section--game-cta");
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    window.addEventListener("tlr-game:buy", (e) => this.openCheckout(e.detail?.pack));

    // 📱 Bouton "Quitter le plein écran" dans le stage (mobile only)
    const stage = this.closest(".tlr-game-arena__stage");
    stage?.querySelector("[data-tlr-exit-fs]")?.addEventListener("click", () => {
      // Quitting mid-run CLOSES the run server-side: going straight back to the menu
      // burned the credit and never called /run/finish. The server arbitrates the score.
      if (this.state === STATE.PLAYING && this.runId) {
        this.endGame();
        this.exitFullscreenMobile(); // endGame keeps fullscreen: the ✕ is the request to leave
        return;
      }
      this.exitFullscreenMobile();
      // Bonus UX : retour au menu pour qu'on puisse re-cliquer JOUER
      if (this.state === STATE.PLAYING) {
        if (this.gameLoop) cancelAnimationFrame(this.gameLoop);
        this.gameLoop = null;
        this.stopMusic();
        this.state = STATE.MENU;
        this.applyState();
      }
    });
  }

  /* ──────────────────────────────────────────
   * 📱 Fullscreen landscape mobile
   * ────────────────────────────────────────── */

  isMobileViewport() {
    return mqMatches("(max-width: 1024px)");
  }

  async enterFullscreenMobile() {
    if (!this.isMobileViewport()) return;
    const stage = this.closest(".tlr-game-arena__stage");
    if (!stage) return;
    // Idempotent: a second pass would snapshot the already hidden widgets as
    // display:none and restore them hidden on exit.
    if (stage.classList.contains("tlr-game-arena__stage--fullscreen-mobile")) return;

    // 🚪 Portal : on déplace le stage directement dans <body> pour échapper à
    //    tout containing block parent (transform/filter/perspective) qui empêcherait
    //    `position: fixed` de couvrir le viewport entier. Sans ça, le stage reste
    //    confiné à son parent → canvas étroit, sprite paraît énorme.
    if (stage.parentNode !== document.body) {
      this._stageOrigParent = stage.parentNode;
      this._stageOrigNextSibling = stage.nextSibling;
      this._portalling = true;
      document.body.appendChild(stage);
      this._portalling = false;
    }

    stage.classList.add("tlr-game-arena__stage--fullscreen-mobile");
    document.body.classList.add("tlr-game-fs-active"); // fallback bg noir browsers sans :has()

    // 🫥 Masque tous les widgets flottants (position: fixed/sticky) hors du stage —
    //    évite WhatsApp/Weedélite/popups d'apparaître au-dessus du jeu et capturer le touch
    //    (ce qui déclenche le menu Copier iOS quand le doigt touche leur texte)
    this._hiddenForFs = [];
    document.querySelectorAll("body *").forEach((el) => this.hideIfFloating(el, stage));

    // ⏱ Un instantané ne voit que ce qui est déjà là. Les widgets d'apps se posent
    //    APRÈS le chargement (Axeptio se plaçait au-dessus du jeu), et certains se
    //    re-créent. On continue donc de surveiller les ajouts directs à <body> tant
    //    que le plein écran dure.
    if (!this._fsWidgetObserver && typeof MutationObserver === "function") {
      this._fsWidgetObserver = new MutationObserver((records) => {
        const live = this.closest(".tlr-game-arena__stage");
        if (!live || !live.classList.contains("tlr-game-arena__stage--fullscreen-mobile")) return;
        for (const rec of records) {
          rec.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            this.hideIfFloating(node, live);
          });
        }
      });
      // subtree: an app widget often mounts inside its own wrapper, not straight into
      // <body>. hideIfFloating checks `stage.contains` BEFORE getComputedStyle, so the
      // game never pays a style recalculation for its own mutations.
      this._fsWidgetObserver.observe(document.body, { childList: true, subtree: true });
    }

    // 1. Fullscreen API (Chrome Android : OK · iOS Safari : ignore silencieusement)
    const el = stage;
    const reqFs = el.requestFullscreen || el.webkitRequestFullscreen || el.webkitEnterFullscreen;
    if (reqFs) {
      try { await reqFs.call(el, { navigationUI: "hide" }); } catch (_) { /* user gesture manquant ou non supporté */ }
    }

    // 2. Lock orientation landscape (Chrome Android : OK · iOS Safari : pas dispo)
    if (screen.orientation && typeof screen.orientation.lock === "function") {
      try { await screen.orientation.lock("landscape"); } catch (_) { /* iOS Safari → overlay "tourne ton tel" prendra le relais */ }
    }

    // 3. Recalc canvas + recompute player size quand orientation/resize change
    if (!this._orientationHandler) {
      this._orientationHandler = () => {
        if (this.canvas) this.resizeCanvas();
        this.recomputePlayerSize();
        this.refreshRotateBlock();
        this.hideUrlBarIos(); // Re-scroll : Safari peut révéler la barre au resize
      };
      window.addEventListener("orientationchange", this._orientationHandler);
      window.addEventListener("resize", this._orientationHandler);
    }
    // Recalc immédiat (après que le CSS fullscreen ait pris effet)
    requestAnimationFrame(() => {
      if (this.canvas) this.resizeCanvas();
      this.recomputePlayerSize();
      this.refreshRotateBlock();
    });
    setTimeout(() => {
      if (this.canvas) this.resizeCanvas();
      this.recomputePlayerSize();
      this.refreshRotateBlock();
    }, 250);

    // 4. 🍎 Scroll trick iOS Safari : force scroll d'1px → Safari masque la barre URL.
    //    C'est le seul moyen en mode browser (sans PWA standalone) de minimiser l'UI Safari.
    this.hideUrlBarIos();
  }

  /**
   * Hides one floating widget (fixed or sticky) for the duration of the fullscreen, and
   * remembers what its display was so the exit can put it back exactly as it found it.
   */
  hideIfFloating(el, stage) {
    if (!el || el === stage || stage.contains(el) || el.contains(stage)) return;
    // `el === stage` above is what actually protects the stage during the portal: a
    // MutationObserver callback runs at the microtask checkpoint, by which time
    // _portalling is false again. Kept for the synchronous caller (the entry sweep).
    if (this._portalling) return;
    // The game speaks through <body> too (the reward failure toast): hiding every
    // floating element must never hide the one that carries a money-path message.
    if (el.hasAttribute?.("data-tlr-game-ui")) return;
    let cs;
    try { cs = window.getComputedStyle(el); } catch (_) { return; }
    if (cs.position !== "fixed" && cs.position !== "sticky") return;
    if (!Array.isArray(this._hiddenForFs)) this._hiddenForFs = [];
    if (this._hiddenForFs.some((h) => h.el === el)) return;
    this._hiddenForFs.push({ el, prevDisplay: el.style.display });
    el.style.display = "none";
  }

  /**
   * Hack iOS Safari : ajoute temporairement 1px de scrollable et scroll
   * pour forcer Safari à masquer la barre URL du haut.
   * En mode standalone (PWA) ou desktop : no-op.
   */
  hideUrlBarIos() {
    const isStandalone =
      mqMatches("(display-mode: standalone)") ||
      mqMatches("(display-mode: fullscreen)") ||
      window.navigator.standalone === true;
    if (isStandalone || !this.isMobileViewport()) return;

    // Inerte : sur iOS moderne, un scrollTo() programmatique RÉVÈLE la barre au lieu de la
    // cacher. On laisse Safari gérer (masquage natif en paysage). Garde l'annulation des minuteurs hérités.
    this.cancelUrlBarTimers();
  }

  /** The url-bar scrollTo series, dropped wherever it would fight the page's own scroll. */
  cancelUrlBarTimers() {
    if (!Array.isArray(this._urlBarTimers)) return;
    this._urlBarTimers.forEach((id) => clearTimeout(id));
    this._urlBarTimers = [];
  }

  exitFullscreenMobile() {
    // Leaving fullscreen cancels an armed start: turning the phone later must not
    // open a run the player already walked away from.
    this._pendingStartToken = 0;
    this.cancelUrlBarTimers();
    this.syncStageState();
    const stage = this.closest(".tlr-game-arena__stage");
    if (stage) stage.classList.remove("tlr-game-arena__stage--fullscreen-mobile");
    document.body.classList.remove("tlr-game-fs-active");

    // 🍎 Reset le scroll trick iOS
    document.body.style.minHeight = "";

    // 🔁 Arrêter la veille et restaurer les widgets masqués à l'entrée ET pendant la partie
    this.releaseFsWidgets();

    // 🚪 Retour du stage à sa place originale dans le DOM
    if (stage && this._stageOrigParent) {
      this._portalling = true;
      if (this._stageOrigNextSibling && this._stageOrigNextSibling.parentNode === this._stageOrigParent) {
        this._stageOrigParent.insertBefore(stage, this._stageOrigNextSibling);
      } else {
        this._stageOrigParent.appendChild(stage);
      }
      this._portalling = false;
      this._stageOrigParent = null;
      this._stageOrigNextSibling = null;
    }

    const exitFs = document.exitFullscreen || document.webkitExitFullscreen;
    if (exitFs && (document.fullscreenElement || document.webkitFullscreenElement)) {
      try { exitFs.call(document); } catch (_) {}
    }
    if (screen.orientation && typeof screen.orientation.unlock === "function") {
      try { screen.orientation.unlock(); } catch (_) {}
    }
    if (this._orientationHandler) {
      window.removeEventListener("orientationchange", this._orientationHandler);
      window.removeEventListener("resize", this._orientationHandler);
      this._orientationHandler = null;
    }
    requestAnimationFrame(() => {
      if (this.canvas) this.resizeCanvas();
      this.refreshRotateBlock();
    });

    // Fullscreen leaves the page where it stood — the iOS url-bar trick scrolled it to
    // the top, so the arena sits below the fold. Whoever asked to leave (the ✕, a reward
    // claim, the packs screen) asked to leave the FULLSCREEN, not the game: the card
    // they still need comes back under them. No-op when it is already on screen.
    this.keepStageInView();
  }

  /* ──────────────────────────────────────────
   * Portrait overlay : a run never advances behind it
   * ────────────────────────────────────────── */

  /**
   * The rotate overlay sits above the canvas and eats every tap: a run left running
   * under it was consumed and lost without the player ever seeing a frame. The state
   * is read from the RENDERED overlay (its visibility is a pure CSS matter), and only
   * on the events that can change it — never once per frame.
   */
  refreshRotateBlock() {
    const el = this.refs?.rotatePrompt;
    const blocked = !!el && window.getComputedStyle(el).display !== "none";
    if (blocked === this._rotateBlocked) return;
    this._rotateBlocked = blocked;
    if (blocked) {
      this.pauseRun();
      return;
    }
    if (this._pendingStartToken) {
      // The run was armed but never opened: the server call happens now, and the
      // player gets the normal countdown on a canvas they can actually see.
      const token = this._pendingStartToken;
      this._pendingStartToken = 0;
      this._pausedAt = 0;
      this.beginRun(token).catch((err) => console.warn("[TEALER RUN] beginRun failed", err));
      return;
    }
    this.resumeRun();
  }

  /**
   * Predicate used at click time, before the overlay is on screen: its display is
   * still none, so the media query that drives it is the only source of truth yet.
   */
  isRotateBlockedAtStart() {
    if (this._rotateBlocked) return true;
    return this.isMobileViewport() && mqMatches("(orientation: portrait)");
  }

  /** Freezes the run: no update, no elapsed time, no music. */
  pauseRun() {
    if (this._pausedAt) return;
    this._pausedAt = performance.now();
    this.thrusting = false;
    this.stopMusic();
  }

  /** Resumes where the run stopped: every absolute deadline moves with the pause. */
  resumeRun() {
    if (!this._pausedAt) return;
    const delta = performance.now() - this._pausedAt;
    this._pausedAt = 0;
    this.shiftRunTimers(delta);
    if (this.state === STATE.PLAYING && !this.muted) this.startMusic();
  }

  /**
   * The engine reasons in absolute timestamps (countdown, spawns, power-ups, i-frames).
   * Shifting them all by the paused duration is what actually freezes elapsed time —
   * resetting lastFrameTime alone would let every timer fire at once on resume.
   */
  shiftRunTimers(delta) {
    const keys = [
      "gameStartTime", "lastFrameTime", "countdownStartedAt", "countdownEndsAt", "_gameplayStartedAt",
      "boostUntil", "invincibleUntil", "spawnGraceUntil", "shieldUntil", "magnetUntil", "doubleScoreUntil", "slowmoUntil",
      "shakeUntil", "hitstopUntil", "flashUntil",
      "lastSpawnAt", "lastStarAt", "lastPowerupAt", "lastHeartAt", "lastDroneAt", "lastLaserAt",
    ];
    for (const key of keys) {
      if (typeof this[key] === "number" && this[key] > 0) this[key] += delta;
    }
    this.cops?.forEach((c) => { if (c.lastShotAt) c.lastShotAt += delta; });
  }

  /* ──────────────────────────────────────────
   * State machine
   * ────────────────────────────────────────── */

  applyState() {
    const visibleKey = {
      [STATE.MENU]: "menu",
      [STATE.GAMEOVER]: "gameover",
      [STATE.NO_CREDITS]: "noCredits",
      [STATE.PLAYING]: null,
    }[this.state];
    ["menu", "gameover", "noCredits"].forEach((key) => {
      const el = this.refs[key];
      if (!el) return;
      if (key === visibleKey) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "");
    });
    if (this.refs.hud) {
      if (this.state === STATE.PLAYING) this.refs.hud.removeAttribute("hidden");
      else this.refs.hud.setAttribute("hidden", "");
    }
    this.syncStageState();
  }

  /**
   * Mirrors the run state on the stage, for the CSS that lives outside <tlr-game>:
   * the "turn your phone" overlay must not cover the gameover card in portrait. An
   * armed start counts as playing — the run is waiting for the phone to turn, and the
   * overlay is the only thing telling the player so.
   */
  syncStageState() {
    const stage = this.closest(".tlr-game-arena__stage");
    if (!stage) return;
    stage.dataset.tlrState = this._pendingStartToken ? STATE.PLAYING : this.state;
  }

  /* ──────────────────────────────────────────
   * Game flow
   * ────────────────────────────────────────── */

  /**
   * Handles the PLAY click. A run is never OPENED while it cannot be seen: in mobile
   * portrait the rotate overlay goes up first and the server call is armed for the
   * landscape. A player who leaves without turning the phone has spent nothing, so
   * there is no credit to refund.
   */
  async startGame() {
    if (this._starting) return;
    this._starting = true;
    this._runToken += 1;
    const token = this._runToken;
    this.runId = null;
    this._pendingStartToken = 0;
    try {
      if (this._statePromise) {
        await this._statePromise;
        if (token !== this._runToken) return;
      }

      // Demo caused by a transient outage at load (not a real guest): one more attempt
      // to reach the server before starting, so a signed-in player never plays a run
      // that spends nothing and rewards nothing after a slow first /state. A single try
      // (retry:false): the initial load already retried, and PLAY must not stall on a
      // second doubled timeout while the App Proxy is down — the run then opens in demo.
      if (this.demoMode && this._demoOutage) {
        this._statePromise = this.loadServerState({ retry: false });
        await this._statePromise;
        if (token !== this._runToken) return;
      }

      if (this.isRotateBlockedAtStart()) {
        this._pausedAt = 0; // nothing is running yet: this is an armed start, not a pause
        await this.enterFullscreenMobile();
        if (token !== this._runToken) return;
        this._rotateBlocked = true;
        this._pendingStartToken = token;
        this.syncStageState();
        return;
      }

      await this.beginRun(token);
    } finally {
      this._starting = false;
    }
  }

  /**
   * Opens the run for real. The server decides whether it is playable and what it costs:
   *   ok          → credits already debited and returned by /run/start
   *   no-credits  → 402, the "out of games" screen with the packs
   *   demo        → guest, theme editor or unreachable API: playable, nothing at stake
   */
  async beginRun(token) {
    this.setLoading(true);
    try {
      if (!this.demoMode) {
        const outcome = await this.startRun(token);
        if (token !== this._runToken) return; // a newer run took over while awaiting
        if (outcome === "stale") return;
        if (outcome === "rate-limited") {
          this.showToast(this.t("rateLimited"));
          return;
        }
        if (outcome === "no-credits") {
          this.state = STATE.NO_CREDITS;
          this.applyState();
          tlrEvents.emit("tlr_game_no_credits_shown", { customer_id: this.customerId });
          return;
        }
      }

      // Media are pulled here, not at connectedCallback: Paris now, the next city as
      // the player gets close to it (see tick).
      const runImages = [...this.loadSharedSprites(), ...this.ensureCityAssets(0)];
      await this.preloadRunAssets(runImages);
      if (token !== this._runToken) return; // the player relaunched or left while loading

      this.session.gamesPlayed += 1;
      this.saveSession();

      this.score = 0;
      this.collectedCount = 0;
      this.lives = 3;
      this._lifeSlots = 3;
      this.distance = 0;
      this.cityIdx = 0;
      this.flag = CITIES[0].flag;

      this.bgOffset = 0;
      this.cops = [];
      this.pochons = [];
      this.stars = [];
      this.smoke = [];
      this.powerups = [];        // bouclier / aimant / 2x score / slow-mo
      this.drones = [];          // ennemis volants qui suivent le perso (à partir de Berlin)
      this.lasers = [];          // obstacles statiques (laser/barbelé) à hauteur fixe
      this.bullets = [];         // projectiles tirés par les flics (lasers mobiles)
      this.boss = null;          // helicopter boss à la fin de chaque ville
      this.bossSpawned = [false, false, false, false]; // par ville
      this.floatingTexts = [];   // textes flottants "+15", "COMBO!", etc.
      this.achievementsShown = new Set(); // pour pas afficher 2x le même
      this.lastSpawnAt = 0;
      this.lastStarAt = 0;
      this.lastPowerupAt = 0;
      this.lastDroneAt = 0;
      this.lastLaserAt = 0;
      this.boostUntil = 0;
      this.shieldUntil = 0;
      this.magnetUntil = 0;
      this.doubleScoreUntil = 0;
      this.slowmoUntil = 0;
      this.shakeUntil = 0;
      this.shakeIntensity = 0;
      this.hitstopUntil = 0;
      this.flashUntil = 0;
      this.thrusting = false;
      this._worldTourDone = false;
      this._gameplayStartedAt = 0;
      // Combo : compteur de pochons collectés d'affilée sans dégât
      this.combo = 0;
      this.maxComboThisGame = 0;
      // Bonus vie : ❤️ qui spawn périodiquement (22-37s) pendant la partie
      this.hearts = [];
      this.lastHeartAt = performance.now() + 3500 + 12000 + Math.random() * 10000; // premier ~12-22s après start
      this.maxLives = 5; // 3 vies de base + jusqu'à 2 bonus via cœurs

      // Countdown 3-2-1-GO! avant que les physics démarrent.
      // Pendant ce temps : bg dessiné, perso visible et stable (pas de chute),
      // overlay countdown + rappel des contrôles affichés.
      // The rotate overlay can pause the run from the moment PLAY was armed, so a pause
      // may have started BEFORE any of these deadlines existed — and resumeRun shifts
      // every timer by its whole duration. With a loading screen in between, that turned
      // a 400 ms rotation into a 3.9 s countdown. The pause starts counting here, where
      // the countdown starts counting.
      if (this._pausedAt) this._pausedAt = performance.now();
      this.countdownStartedAt = performance.now();
      this.countdownDuration = 3500;       // 3s + 0.5s pour "GO!"
      this.countdownEndsAt = this.countdownStartedAt + this.countdownDuration;
      // Neutral and untouchable until shortly after the GO: the first free fall must
      // not cost a life to a player who has not had a single frame to react. Kept
      // apart from invincibleUntil, which also drives the damage blink.
      this.invincibleUntil = 0;
      this.spawnGraceUntil = this.countdownEndsAt + COUNTDOWN_GRACE_MS;

      this.state = STATE.PLAYING;
      this.applyState();
      this.updateHud();

      // 📱 Mobile : passe en fullscreen landscape PUIS attend que le layout soit appliqué
      //    avant de créer le player. Sinon player.w est calculé avec viewH pré-fullscreen
      //    (gros) → le sprite paraît énorme dans le canvas landscape post-fullscreen.
      await this.enterFullscreenMobile();
      if (token !== this._runToken) return;
      // Force reflow + 2 frames d'attente pour que iOS Safari applique vraiment le CSS fullscreen
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (token !== this._runToken) return;

      tlrEvents.emit("tlr_game_started", {
        customer_id: this.customerId,
        demo: this.demoMode,
        credits_remaining: this.credits,
        games_played: this.session.gamesPlayed,
      });

      // Portrait: the overlay covers the canvas, so the run starts paused and only
      // begins once the phone is turned.
      this.refreshRotateBlock();
      if (!this._pausedAt) this.startMusic();
      this.runGameLoop();
    } finally {
      // Whatever happened — run opened, 402, rate limit, stale token, throw — the screen
      // comes down. It is the only thing between the player and the game.
      this.setLoading(false);
    }
  }

  /* ──────────────────────────────────────────
   * Canvas setup + input
   * ────────────────────────────────────────── */

  /**
   * Échelle de taille des entités. Conservé à 1 : la cohérence du gameplay
   * (taille relative des objets, physique px/frame) est garantie par la hauteur
   * logique fixe LOGICAL_GAME_H. Le ratio écran n'influe que sur la largeur
   * logique (champ de vision), pas sur la taille des objets.
   */
  get sizeScale() {
    return 1;
  }

  /**
   * Recalcule la taille du player après un changement de viewport (orientation change, fullscreen).
   * Les objets en jeu (cops/pochons/etc) gardent leur taille à la création mais le player est l'objet
   * le plus visible — on le réajuste pour qu'il reste proportionnel au canvas actuel.
   */
  recomputePlayerSize() {
    if (!this.player || !this.canvas) return;
    const h = this.viewH();
    const s = this.sizeScale;
    const newSize = Math.min(72, h * 0.13) * s;
    this.player.w = newSize;
    this.player.h = newSize;
  }

  runGameLoop() {
    if (!this.canvas) {
      this.canvas = document.createElement("canvas");
      this.canvas.className = "tlr-game__canvas";
      this.canvas.setAttribute("role", "img");
      this.canvas.setAttribute("aria-label", this.t("canvasLabel"));
      this.canvas.tabIndex = 0; // keyboard players need to be able to focus the stage
      this.insertBefore(this.canvas, this.refs.hud);
      this.ctx = this.canvas.getContext("2d");
    }
    this.resizeCanvas();
    if (!this.inputBound) this.bindGameInput();

    const w = this.viewW();
    const h = this.viewH();
    const s = this.sizeScale;
    this.player = {
      x: w * 0.22,
      y: h * 0.45,
      vy: 0,
      w: Math.min(72, h * 0.13) * s,
      h: Math.min(72, h * 0.13) * s,
    };

    // Paint immediate sky background so canvas is never pure black before first tick
    const grad = this.ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, CITIES[0].sky[0]);
    grad.addColorStop(1, CITIES[0].sky[1]);
    this.ctx.fillStyle = grad;
    this.ctx.fillRect(0, 0, w, h);

    this.gameStartTime = performance.now();
    this.lastFrameTime = performance.now();
    // Force first tick synchronously to ensure something renders even if RAF is delayed
    this.tick(performance.now());
  }

  resizeCanvas() {
    const rect = this.getBoundingClientRect();
    // ⚠️ Élément pas encore layouté (transition fullscreen, display:none, etc.) :
    // rect.height ≈ 0 ferait exploser la largeur logique (= 600 × ratio) → freeze.
    // On garde les valeurs précédentes ; les re-resize (rAF/timeout/resize) corrigeront.
    if (rect.width < 40 || rect.height < 40) return;
    this.canvas.width  = Math.max(1, rect.width  * this.dpr);
    this.canvas.height = Math.max(1, rect.height * this.dpr);
    this.canvas.style.width  = rect.width  + "px";
    this.canvas.style.height = rect.height + "px";
    // Hauteur logique FIXE : on contraint la hauteur de jeu à LOGICAL_GAME_H quelle
    // que soit la taille réelle du canvas, et on scale le rendu via le contexte. La
    // largeur logique suit le ratio (gérée par viewW). → physique + tailles cohérentes
    // partout, contrôle vertical jamais "nerveux" sur un canvas plat (fix injouabilité).
    let scale = rect.height / LOGICAL_GAME_H;
    // Plafond de largeur logique : empêche un viewW délirant sur écran ultra-large
    // ou rect mal mesuré (au pire on rogne un peu la hauteur sur ces cas extrêmes).
    const LOGICAL_W_MAX = 1280;
    if (rect.width / scale > LOGICAL_W_MAX) scale = rect.width / LOGICAL_W_MAX;
    this._logicalScale = scale;
    const s = this.dpr * scale;
    this.ctx.setTransform(s, 0, 0, s, 0, 0);
  }

  viewW() { return this.canvas.width  / (this.dpr * this._logicalScale); }
  viewH() { return this.canvas.height / (this.dpr * this._logicalScale); }

  bindGameInput() {
    this.inputBound = true;
    const start = (e) => {
      if (this.state !== STATE.PLAYING) return;
      if (e.cancelable) e.preventDefault();
      this.thrusting = true;
    };
    const end = (e) => {
      if (this.state !== STATE.PLAYING) return;
      if (e && e.cancelable) e.preventDefault();
      this.thrusting = false;
    };
    this.canvas.addEventListener("pointerdown", start);
    document.addEventListener("pointerup", end);
    document.addEventListener("pointercancel", end);
    this.canvas.addEventListener("pointerleave", end);

    // KeyZ : AZERTY keyboards put W where QWERTY puts Z — both map to thrust.
    const THRUST_KEYS = new Set(["Space", "ArrowUp", "KeyW", "KeyZ"]);
    // A key aimed at a real control (the focused REJOUER / PARTAGER button) belongs to
    // the browser: swallowing Space there would make the card unusable at the keyboard.
    const FOCUSABLE = "button, a[href], input, select, textarea, [contenteditable]";
    this.keyHandler = (e) => {
      if (!THRUST_KEYS.has(e.code)) return;
      if (this.state === STATE.PLAYING) {
        if (e.cancelable) e.preventDefault();
        this.thrusting = (e.type === "keydown");
        return;
      }
      // Gameover card: the run is over, the reflex is not. Space and the arrows scroll
      // the page by default, so a player still mashing thrust was carried to the bottom
      // of the page, away from the card that holds the reward. They are swallowed here —
      // never turned into a thrust, and never into a replay, which would burn a credit.
      //
      // Only while the arena is on screen, and only for the key on its own. The state
      // stays GAMEOVER until a replay or a reload: capturing on the state alone would
      // kill the spacebar for the whole page, for the rest of the visit — a player who
      // scrolls down to the packs could never page down again.
      if (this.state !== STATE.GAMEOVER) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.target?.closest?.(FOCUSABLE)) return;
      if (!this.isStageInView()) return;
      if (e.cancelable) e.preventDefault();
    };
    window.addEventListener("keydown", this.keyHandler);
    window.addEventListener("keyup", this.keyHandler);

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.thrusting = false;
    });
  }

  /* ──────────────────────────────────────────
   * Main tick : update + draw
   * ────────────────────────────────────────── */

  tick(now) {
    if (this.state !== STATE.PLAYING) return;

    // Portrait overlay up: keep the frame loop alive but freeze the run entirely —
    // no update, no elapsed time, no spawn. resumeRun() shifts every deadline back.
    if (this._pausedAt) {
      this.gameLoop = requestAnimationFrame((n) => this.tick(n));
      return;
    }

    if (now < this.hitstopUntil) {
      this.draw(now, 0);
      this.applyScreenShake(now);
      this.gameLoop = requestAnimationFrame((n) => this.tick(n));
      return;
    } else if (this.canvas) {
      this.canvas.style.transform = ""; // reset shake transform
    }

    const dt = Math.min(33, now - this.lastFrameTime) / 16.67; // normalize 60fps
    this.lastFrameTime = now;

    const w = this.viewW();
    const h = this.viewH();
    const inCountdown = now < this.countdownEndsAt;

    if (inCountdown) {
      // Forcer le perso à rester en place (pas de chute, pas d'input)
      this.player.vy = 0;
      this.player.y = h * 0.45;
      this.thrusting = false;
      // Léger défilement du bg pour donner l'impression que ça bouge
      this.bgOffset += 1.5 * dt;
      // Dessine la scène + overlay countdown
      this.draw(now, 0);
      this.drawCountdown(this.ctx, w, h, now);
      this.gameLoop = requestAnimationFrame((n) => this.tick(n));
      return;
    }

    // Reset gameStartTime à la fin du countdown pour que elapsed démarre à 0
    if (!this._gameplayStartedAt || this._gameplayStartedAt < this.countdownEndsAt) {
      this._gameplayStartedAt = this.countdownEndsAt;
      this.gameStartTime = this.countdownEndsAt;
    }
    const elapsed = (now - this.gameStartTime) / 1000;
    const boostMul = now < this.boostUntil ? 1.6 : 1;
    // Vitesse de défilement : courbe d'origine (douce au départ, montée progressive).
    // La difficulté vient des ENNEMIS (densité + flics + balles), pas de la vitesse.
    const gameSpeed = Math.min(13, 4.2 + elapsed * 0.16) * boostMul;

    this.distance += gameSpeed * dt * 0.45;
    this.bgOffset += gameSpeed * dt;

    // Durée de chaque niveau (en "mètres" de distance).
    // Paris→Tokyo : 7500m chacune. Overdrive : run final infini pour le leaderboard.
    const CITY_LENGTHS = [7500, 7500, 7500, 7500, Infinity];
    let newCityIdx = 0;
    let cumDist = 0;
    for (let i = 0; i < CITY_LENGTHS.length; i++) {
      cumDist += CITY_LENGTHS[i];
      if (this.distance < cumDist) { newCityIdx = i; break; }
      newCityIdx = i + 1;
    }
    newCityIdx = Math.min(4, newCityIdx);
    // Fetch the next city's media as it comes into range (~80 % of the current one),
    // so the switch never lands on a blank parallax.
    if (newCityIdx < 3) {
      const start = cumDist - CITY_LENGTHS[newCityIdx];
      if ((this.distance - start) / CITY_LENGTHS[newCityIdx] > 0.8) this.ensureCityAssets(newCityIdx + 1);
    }
    if (newCityIdx !== this.cityIdx) {
      this.ensureCityAssets(newCityIdx);
      this.cityIdx = newCityIdx;
      this.flag = CITIES[this.cityIdx].flag;
      // Toast en bas : nouvelle ville débloquée
      const cityNames = ["PARIS", "BERLIN", "MOSCOU", "TOKYO", "OVERDRIVE"];
      const cityName = cityNames[this.cityIdx] || "?";
      const toastColor = this.cityIdx === 4 ? "#ffd700" : "#d8ff5a";
      this.addFloatingText(w / 2, h - 70, `${this.flag} ${cityName} UNLOCKED`, toastColor, 1.6);
      this.playSfx("achievement", 0.25);
    }

    // 🏆 Bonus toast à l'entrée dans Overdrive (= tour du monde complet)
    if (this.cityIdx === 4 && !this._worldTourDone) {
      this._worldTourDone = true;
      this.addFloatingText(w / 2, h - 160, `🏆 TOUR DU MONDE COMPLÉTÉ — RUN INFINI`, "#ffd744", 2.4);
      this.playSfx("achievement", 0.4);
    }

    this.player.vy += GRAVITY * dt;
    if (this.thrusting) this.player.vy += THRUST * dt;
    this.player.vy = Math.max(VY_MIN, Math.min(VY_MAX, this.player.vy));
    this.player.y += this.player.vy * dt;

    const minY = 16;
    const maxY = h - this.player.h - 60;
    if (this.player.y < minY) {
      this.player.y = minY;
      this.player.vy = Math.max(0, this.player.vy);
    }
    if (this.player.y > maxY) {
      this.player.y = maxY;
      this.player.vy = 0;
      this.tryDamage(now, "ground");
    }

    // Densité relevée : spawn plus rapide + ratio flics plafonné à 66%.
    if (this.lastSpawnAt === 0) this.lastSpawnAt = now - 800; // premier spawn rapide
    const spawnInterval = Math.max(520, 1500 - elapsed * 30); // ms entre spawns
    if (now - this.lastSpawnAt >= spawnInterval) {
      const copRatio = Math.min(0.66, 0.33 + elapsed * 0.009);
      if (Math.random() < copRatio) this.spawnCop(w, h);
      else this.spawnPochon(w, h);
      this.lastSpawnAt = now;
    }
    if (this.lastStarAt === 0) this.lastStarAt = now;
    const starInterval = 7000 + Math.random() * 5000;
    if (now - this.lastStarAt >= starInterval) {
      this.spawnStar(w, h);
      this.lastStarAt = now;
    }
    if (this.lastPowerupAt === 0) this.lastPowerupAt = now;
    const powerupInterval = 15000 + Math.random() * 10000;
    if (now - this.lastPowerupAt >= powerupInterval) {
      this.spawnPowerup(w, h);
      this.lastPowerupAt = now;
    }
    if (now >= this.lastHeartAt && this.lives < this.maxLives) {
      this.spawnHeart(w, h);
      // Prochain cœur dans 22-37s (2× plus fréquent qu'avant)
      this.lastHeartAt = now + 22000 + Math.random() * 15000;
    } else if (this.lives >= this.maxLives && now >= this.lastHeartAt) {
      // Si déjà max vies au moment du spawn prévu, on reporte de 10s
      this.lastHeartAt = now + 10000;
    }
    if (this.cityIdx >= 1) {
      if (!this.lastDroneAt) this.lastDroneAt = now;
      if (now - this.lastDroneAt >= 13000 + Math.random() * 8000) {
        this.spawnDrone(w, h);
        this.lastDroneAt = now;
      }
    }
    if (this.cityIdx >= 2) {
      if (!this.lastLaserAt) this.lastLaserAt = now;
      if (now - this.lastLaserAt >= 16000 + Math.random() * 10000) {
        this.spawnLaser(w, h);
        this.lastLaserAt = now;
      }
    }
    // Overdrive (cityIdx=4) = run final, boss déjà spawné à l'entrée → on saute la logique
    const cityStarts = [0, 7500, 15000, 22500, 30000];
    const cityEnds   = [7500, 15000, 22500, 30000, Infinity];
    if (this.cityIdx < 4 && !this.bossSpawned[this.cityIdx]) {
      const cityProgress = (this.distance - cityStarts[this.cityIdx]) / (cityEnds[this.cityIdx] - cityStarts[this.cityIdx]);
      if (cityProgress > 0.88) {
        this.spawnBoss(w, h);
        this.bossSpawned[this.cityIdx] = true;
      }
    }

    // Slow-mo affecte tout sauf le perso (mais en vrai on ralentit aussi gameSpeed)
    const slowMul = now < this.slowmoUntil ? 0.4 : 1;
    const entitySpeed = gameSpeed * 1.4 * slowMul;
    this.cops.forEach((c) => {
      c.x -= entitySpeed * dt;
      c.bob += dt * 0.08;
      // Chaque flic tire un laser de temps en temps dès qu'il est visible
      // (toute la zone canvas, pas juste le centre).
      if (c.x > -c.w * 0.5 && c.x < w + 10) {
        if (!c.lastShotAt) c.lastShotAt = now + 400 + Math.random() * 800; // délai initial 0.4-1.2s
        if (now > c.lastShotAt) {
          this.fireCopBullet(c, now);
          // cooldown court : raccourcit avec la difficulté (flics plus agressifs)
          const baseCooldown = Math.max(800, 1900 - elapsed * 22);
          c.lastShotAt = now + baseCooldown + Math.random() * 1000;
        }
      }
    });
    this.pochons.forEach((p) => { p.x -= entitySpeed * dt; p.spin += dt * 0.05; });
    this.stars.forEach((s) => { s.x -= entitySpeed * dt; s.spin += dt * 0.1; });
    this.hearts.forEach((h) => { h.x -= entitySpeed * dt; h.spin += dt * 0.08; });
    this.powerups.forEach((p) => { p.x -= entitySpeed * dt; p.spin += dt * 0.06; });
    this.lasers.forEach((l) => { l.x -= entitySpeed * dt; l.pulse += dt * 0.15; });
    // Bullets : vont selon leur vecteur de tir (vx, vy) qui pointe vers le perso au moment du tir
    this.bullets.forEach((b) => {
      b.x += b.vx * dt;
      b.y += b.vy * dt;
      b.life -= dt;
    });
    // Drones : suivent la verticale du perso lentement
    this.drones.forEach((d) => {
      d.x -= entitySpeed * 0.85 * dt; // un peu plus lent que les flics
      const targetY = this.player.y;
      const dy = targetY - d.y;
      d.y += Math.sign(dy) * Math.min(2 * dt, Math.abs(dy));
      d.bob += dt * 0.1;
    });
    // Boss : balaye l'écran horizontalement, sinus vertical
    if (this.boss) {
      this.boss.x -= entitySpeed * 0.55 * dt;
      this.boss.y = this.boss.baseY + Math.sin(this.boss.x * 0.005) * 80;
      if (this.boss.x < -250) this.boss = null;
    }

    // Aimant : attire les pochons proches vers le perso
    if (now < this.magnetUntil) {
      const px = this.player.x + this.player.w / 2;
      const py = this.player.y + this.player.h / 2;
      const radius = 220;
      this.pochons.forEach((p) => {
        const dx = px - (p.x + p.w / 2);
        const dy = py - (p.y + p.h / 2);
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < radius && d > 1) {
          const pull = 10 * dt;
          p.x += (dx / d) * pull;
          p.y += (dy / d) * pull;
        }
      });
    }

    // Floating texts update
    this.floatingTexts.forEach((t) => {
      t.y += t.vy * dt;
      t.vy *= 0.96; // ralentit
      t.life -= dt;
    });
    this.floatingTexts = this.floatingTexts.filter((t) => t.life > 0);

    this.cops     = this.cops.filter((c) => c.x > -120);
    this.pochons  = this.pochons.filter((p) => p.x > -80);
    this.stars    = this.stars.filter((s) => s.x > -80);
    this.hearts   = this.hearts.filter((h) => h.x > -80);
    this.powerups = this.powerups.filter((p) => p.x > -80);
    this.drones   = this.drones.filter((d) => d.x > -120);
    this.lasers   = this.lasers.filter((l) => l.x > -300);
    this.bullets  = this.bullets.filter((b) => b.life > 0 && b.x > -50 && b.x < w + 50 && b.y > -50 && b.y < h + 50);

    // prefers-reduced-motion: fewer particles, and applyScreenShake stops shaking.
    const smokePerFrame = this.reducedMotion ? 1 : 2;
    if (this.thrusting) {
      for (let i = 0; i < smokePerFrame; i++) {
        this.smoke.push({
          x: this.player.x - this.player.w * 0.15 + (Math.random() - 0.5) * 8,
          y: this.player.y + this.player.h * 0.7 + (Math.random() - 0.5) * 6,
          vx: -1.5 - Math.random() * 1.5,
          vy: 0.5 + Math.random() * 1.2,
          life: 26 + Math.random() * 14,
          size: 6 + Math.random() * 6,
        });
      }
    }
    this.smoke.forEach((s) => {
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.life -= dt;
      s.size += 0.35 * dt;
    });
    this.smoke = this.smoke.filter((s) => s.life > 0);

    this.checkCollisions(now);

    this.score = Math.floor(this.distance * 0.5) + this.collectedCount * 15;

    if (this.distance >= 1500) this.checkAchievement("distance_1500");
    if (this.distance >= 7500) this.checkAchievement("distance_7500");
    // 30s sans dégât = achievement
    if (this.invincibleUntil === 0 || now - this.invincibleUntil > 30000) {
      // utiliser elapsed comme proxy
      if (elapsed >= 30) this.checkAchievement("no_hit_30s");
    }

    this.draw(now, gameSpeed);
    this.applyScreenShake(now);
    this.updateHud();

    this.gameLoop = requestAnimationFrame((n) => this.tick(n));
  }

  /**
   * Applique le screen shake en transformant le canvas en CSS.
   * Plus rapide que de translater le ctx (n'affecte pas les coords des hitboxes).
   */
  applyScreenShake(now) {
    if (!this.canvas) return;
    if (this.reducedMotion) {
      if (this.canvas.style.transform) this.canvas.style.transform = "";
      return;
    }
    if (now < this.shakeUntil) {
      const intensity = this.shakeIntensity * ((this.shakeUntil - now) / 220);
      const dx = (Math.random() - 0.5) * intensity * 2;
      const dy = (Math.random() - 0.5) * intensity * 2;
      this.canvas.style.transform = `translate(${dx}px, ${dy}px)`;
    } else if (this.canvas.style.transform) {
      this.canvas.style.transform = "";
    }
  }

  /* ──────────────────────────────────────────
   * Spawn helpers
   * ────────────────────────────────────────── */

  spawnCop(w, h) {
    const size = Math.min(64, h * 0.11) * this.sizeScale;
    const lane = Math.random();
    let y;
    // Spawn in cluster (1-3 cops vertically) at higher difficulty
    const elapsed = (performance.now() - this.gameStartTime) / 1000;
    if (lane < 0.3) y = h * 0.15 + Math.random() * 60;
    else if (lane < 0.7) y = h * 0.4 + Math.random() * 60;
    else y = h * 0.65 + Math.random() * 40;
    this.cops.push({
      x: w + 40,
      y,
      w: size,
      h: size,
      bob: Math.random() * Math.PI * 2,
    });
    if (elapsed > 25 && Math.random() < 0.3) {
      // Pair of cops
      this.cops.push({
        x: w + 40 + size * 1.2,
        y: y + size * 1.4,
        w: size,
        h: size,
        bob: Math.random() * Math.PI * 2,
      });
    }
  }

  spawnPochon(w, h) {
    const size = Math.min(46, h * 0.08) * this.sizeScale;
    const groupSize = 1 + Math.floor(Math.random() * 3); // 1-3 pochons en ligne
    const baseY = h * 0.2 + Math.random() * (h * 0.55);
    for (let i = 0; i < groupSize; i++) {
      this.pochons.push({
        x: w + 40 + i * (size + 8),
        y: baseY + (Math.random() - 0.5) * 12,
        w: size,
        h: size,
        spin: 0,
      });
    }
  }

  spawnStar(w, h) {
    const size = Math.min(50, h * 0.09) * this.sizeScale;
    this.stars.push({
      x: w + 40,
      y: h * 0.2 + Math.random() * (h * 0.5),
      w: size,
      h: size,
      spin: 0,
    });
  }

  spawnHeart(w, h) {
    const size = Math.min(54, h * 0.1) * this.sizeScale;
    this.hearts.push({
      x: w + 40,
      y: h * 0.18 + Math.random() * (h * 0.55),
      w: size,
      h: size,
      spin: 0,
    });
    tlrEvents.emit("tlr_game_heart_spawn", { distance: Math.floor(this.distance) });
  }

  spawnPowerup(w, h) {
    const kinds = ["shield", "magnet", "double", "slowmo"];
    const kind = kinds[Math.floor(Math.random() * kinds.length)];
    const size = Math.min(52, h * 0.1) * this.sizeScale;
    this.powerups.push({
      x: w + 40,
      y: h * 0.2 + Math.random() * (h * 0.5),
      w: size,
      h: size,
      spin: 0,
      kind,
    });
  }

  spawnDrone(w, h) {
    const size = Math.min(56, h * 0.1) * this.sizeScale;
    this.drones.push({
      x: w + 40,
      y: h * 0.3 + Math.random() * (h * 0.3),
      w: size,
      h: size,
      bob: Math.random() * Math.PI * 2,
    });
  }

  spawnLaser(w, h) {
    // Laser horizontal à hauteur fixe (force à passer dessus ou dessous)
    const lh = 10;
    this.lasers.push({
      x: w + 100,
      y: h * 0.25 + Math.random() * (h * 0.4),
      w: 220, // largeur du laser
      h: lh,
      pulse: 0,
    });
  }

  /**
   * Un flic tire un laser rouge vers la position actuelle du perso.
   * Le projectile garde son vecteur de tir constant (pas de homing).
   * 30% d'imprécision pour que ce soit esquivable même en restant statique.
   */
  fireCopBullet(cop, now) {
    const cx = cop.x + cop.w * 0.4;       // émis depuis la "main" du flic (vers le perso)
    const cy = cop.y + cop.h * 0.4;
    const tx = this.player.x + this.player.w * 0.5;
    const ty = this.player.y + this.player.h * 0.5;
    // Vecteur vers le perso + spread aléatoire
    const dx = tx - cx;
    const dy = ty - cy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const speed = 10.5; // px / dt-frame — assez rapide pour menacer un perso statique
    const spread = (Math.random() - 0.5) * 0.22; // ±0.11 rad : esquivable en bougeant, mais touche si on ne réagit pas
    const baseAngle = Math.atan2(dy, dx);
    const angle = baseAngle + spread;
    this.bullets.push({
      x: cx,
      y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      r: 5,
      life: 140, // traverse tout le canvas logique avant d'expirer
      trail: [],
    });
    this.playSfx("damage", 0.06); // petit "zap" discret
  }

  spawnBoss(w, h) {
    // Taille du boss : adaptée au sprite (carré-ish, plus gros que l'ancien proc)
    const size = Math.min(180, h * 0.32);
    const cityKey = ["paris", "berlin", "moscow", "hk"][this.cityIdx] || "paris";
    const cityName = ["PARIS", "BERLIN", "MOSCOU", "TOKYO"][this.cityIdx] || "?";
    this.boss = {
      x: w + size,
      y: h * 0.35,
      baseY: h * 0.35,
      w: size,
      h: size,                // ratio carré pour les sprites
      cityKey,                // sprite à utiliser
      enteredAt: performance.now(),
    };
    // Toast d'alerte adapté à la ville
    this.addFloatingText(w / 2, h - 70, `⚠ BOSS ${cityName} INBOUND`, "#ff2d2d", 1.8);
    this.playSfx("boss", 0.32);
  }

  /* ──────────────────────────────────────────
   * Collisions (AABB)
   * ────────────────────────────────────────── */

  checkCollisions(now) {
    const p = this.player;
    const px = p.x + p.w * 0.15;
    const py = p.y + p.h * 0.15;
    const pw = p.w * 0.7;
    const ph = p.h * 0.7;

    // Pochons → collect avec combo multiplicateur
    this.pochons = this.pochons.filter((o) => {
      if (this.aabb(px, py, pw, ph, o.x, o.y, o.w, o.h)) {
        this.combo += 1;
        this.maxComboThisGame = Math.max(this.maxComboThisGame, this.combo);
        const mult = this.getComboMultiplier();
        const basePts = 15;
        const scoreMul = now < this.doubleScoreUntil ? 2 : 1;
        const pts = basePts * mult * scoreMul;
        this.collectedCount += mult * scoreMul;
        this.addFloatingText(o.x + o.w / 2, o.y, `+${pts}`, mult > 1 ? "#ffd744" : "#d8ff5a");
        if (mult >= 2 && this.combo % 5 === 0) {
          this.addFloatingText(this.player.x + this.player.w / 2, this.player.y - 40, `x${mult} COMBO!`, "#ff6a13", 1.4);
          this.checkAchievement("combo_" + mult);
        }
        this.playSfx("pochon", 0.12);
        tlrEvents.emit("tlr_game_pochon_collect", { score: this.score, combo: this.combo });
        return false;
      }
      return true;
    });

    // Stars → boost vitesse + INVINCIBILITÉ 4s (style Mario)
    this.stars = this.stars.filter((o) => {
      if (this.aabb(px, py, pw, ph, o.x, o.y, o.w, o.h)) {
        const mult = this.getComboMultiplier();
        const scoreMul = now < this.doubleScoreUntil ? 2 : 1;
        this.collectedCount += 3 * mult * scoreMul;
        this.boostUntil = now + 4000;
        this.addFloatingText(o.x + o.w / 2, o.y, `+${45 * mult * scoreMul} ⚡`, "#ffd744", 1.3);
        this.addFloatingText(this.player.x + this.player.w / 2, this.player.y - 30, "⭐ INVINCIBLE 4s", "#ffd744", 1.4);
        this.playSfx("star", 0.2);
        tlrEvents.emit("tlr_game_star_collect", { score: this.score });
        return false;
      }
      return true;
    });

    // Power-ups → effets variés
    this.powerups = this.powerups.filter((o) => {
      if (this.aabb(px, py, pw, ph, o.x, o.y, o.w, o.h)) {
        this.applyPowerup(o.kind, now);
        this.playSfx("powerup", 0.32);
        return false;
      }
      return true;
    });

    // Hearts → +1 vie (max maxLives) + feedback son/visuel
    this.hearts = this.hearts.filter((o) => {
      if (this.aabb(px, py, pw, ph, o.x, o.y, o.w, o.h)) {
        if (this.lives < this.maxLives) {
          this.lives += 1;
          this.updateHud();
          this.addFloatingText(o.x + o.w / 2, o.y, "+1 ❤", "#ff5fa8", 1.5);
        } else {
          this.addFloatingText(o.x + o.w / 2, o.y, "❤ MAX", "#ff5fa8", 1.2);
        }
        this.playSfx("heart", 0.3);
        tlrEvents.emit("tlr_game_heart_collect", { lives: this.lives });
        return false;
      }
      return true;
    });

    // Cops → damage
    if (now > this.invincibleUntil) {
      for (const c of this.cops) {
        if (this.aabb(px, py, pw, ph, c.x, c.y, c.w, c.h)) {
          this.tryDamage(now, "cop");
          c.x = -200; // remove the hitting cop
          break;
        }
      }
    }
    // Drones → damage
    if (now > this.invincibleUntil) {
      for (const d of this.drones) {
        if (this.aabb(px, py, pw, ph, d.x, d.y, d.w, d.h)) {
          this.tryDamage(now, "drone");
          d.x = -200;
          break;
        }
      }
    }
    // Lasers → damage (mais on collisionne seulement la zone du laser, pas toute la hauteur)
    if (now > this.invincibleUntil) {
      for (const l of this.lasers) {
        if (this.aabb(px, py, pw, ph, l.x, l.y, l.w, l.h)) {
          this.tryDamage(now, "laser");
          break;
        }
      }
    }
    // Bullets → damage (petite hitbox circulaire)
    if (now > this.invincibleUntil) {
      this.bullets = this.bullets.filter((b) => {
        const dx = (this.player.x + this.player.w * 0.5) - b.x;
        const dy = (this.player.y + this.player.h * 0.5) - b.y;
        const r = this.player.w * 0.35 + b.r;
        if (dx * dx + dy * dy < r * r) {
          this.tryDamage(now, "bullet");
          return false; // bullet consumed
        }
        return true;
      });
    }

    // Boss → damage (hitbox plus grosse)
    if (this.boss && now > this.invincibleUntil) {
      const b = this.boss;
      if (this.aabb(px, py, pw, ph, b.x - b.w * 0.4, b.y - b.h * 0.3, b.w * 0.8, b.h * 0.6)) {
        this.tryDamage(now, "boss");
      }
    }
  }

  aabb(ax, ay, aw, ah, bx, by, bw, bh) {
    return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
  }

  tryDamage(now, source) {
    if (now < this.spawnGraceUntil) return;
    if (now < this.invincibleUntil) return;
    // Boost étoile actif → invincibilité totale (style Mario), pas de dégât
    if (now < this.boostUntil) {
      // Petit flash discret pour signaler qu'on a évité un coup
      this.addFloatingText(this.player.x + this.player.w / 2, this.player.y - 12, "⭐", "#ffd744", 1);
      return;
    }
    // Bouclier actif → absorbe le dégât, pas de vie perdue
    if (now < this.shieldUntil) {
      this.shieldUntil = 0; // bouclier consommé
      this.invincibleUntil = now + 800;
      this.addFloatingText(this.player.x + this.player.w / 2, this.player.y - 20, "🛡 BLOQUÉ", "#06b6d4", 1.3);
      tlrEvents.emit("tlr_game_shield_block", { source });
      return;
    }
    this.lives -= 1;
    this.invincibleUntil = now + 1400;
    // Combo break
    if (this.combo >= 5) {
      this.addFloatingText(this.player.x + this.player.w / 2, this.player.y - 30, `COMBO BREAK!`, "#ff2d2d", 1.3);
    }
    this.combo = 0;
    // Feedback : shake + hitstop + flash rouge + floating text — boostés partout pour
    // un impact ressenti dès Paris (avant on avait du subtle qui passait inaperçu).
    this.shakeUntil = now + 380;
    this.shakeIntensity = 18;
    this.hitstopUntil = now + 90;
    this.flashUntil = now + 320;
    this.addFloatingText(this.player.x + this.player.w / 2, this.player.y, "−1 ❤", "#ff2d2d", 1.4);
    this.playSfx("damage", 0.3);
    tlrEvents.emit("tlr_game_damage", { source, lives_remaining: this.lives });
    if (this.lives <= 0) {
      this.playSfx("gameover", 0.35);
      this.endGame();
    }
  }

  /* ──────────────────────────────────────────
   * Combo multiplicateur
   * combo 0-4 → x1, 5-9 → x2, 10-19 → x3, 20-29 → x4, 30+ → x5
   * ────────────────────────────────────────── */
  getComboMultiplier() {
    const c = this.combo;
    if (c >= 30) return 5;
    if (c >= 20) return 4;
    if (c >= 10) return 3;
    if (c >= 5) return 2;
    return 1;
  }

  /* ──────────────────────────────────────────
   * Power-ups : effets temporaires variés
   * ────────────────────────────────────────── */
  applyPowerup(kind, now) {
    const labels = {
      shield: { text: "🛡 BOUCLIER 5s", color: "#06b6d4", dur: 5000 },
      magnet: { text: "🧲 AIMANT 8s", color: "#ff5fa8", dur: 8000 },
      double: { text: "💎 x2 SCORE 10s", color: "#d8ff5a", dur: 10000 },
      slowmo: { text: "🐢 SLOW-MO 3s", color: "#7b3fe4", dur: 3000 },
    };
    const cfg = labels[kind];
    if (!cfg) return;
    if (kind === "shield") this.shieldUntil = now + cfg.dur;
    else if (kind === "magnet") this.magnetUntil = now + cfg.dur;
    else if (kind === "double") this.doubleScoreUntil = now + cfg.dur;
    else if (kind === "slowmo") this.slowmoUntil = now + cfg.dur;
    // y clampé : si le perso vole tout en haut, le label ne sort pas hors écran.
    const ty = Math.max(this.player.y - 30, 46);
    this.addFloatingText(this.player.x + this.player.w / 2, ty, cfg.text, cfg.color, 1.7);
    tlrEvents.emit("tlr_game_powerup", { kind });
  }

  /* ──────────────────────────────────────────
   * Floating texts (système simple : tableau de {x, y, vy, text, color, life, scale})
   * ────────────────────────────────────────── */
  addFloatingText(x, y, text, color = "#fff", scale = 1) {
    this.floatingTexts.push({
      x, y, vy: -1.2, text, color,
      life: 60, // ~1 seconde à 60fps
      maxLife: 60,
      scale,
    });
    // Limite pour éviter le bloat
    if (this.floatingTexts.length > 30) this.floatingTexts.shift();
  }

  /* ──────────────────────────────────────────
   * Achievements : déclenchement + affichage toast
   * ────────────────────────────────────────── */
  checkAchievement(id) {
    if (this.achievementsShown.has(id)) return;
    const defs = {
      combo_2: "🌿 Streak x2 — Tu chauffes",
      combo_3: "🔥 Streak x3 — Tu charbonnes",
      combo_4: "💎 Streak x4 — Tu fais peur",
      combo_5: "🏆 Streak x5 — Légende vivante",
      distance_1500: "✈️ Première ville traversée",
      distance_7500: "🗼 Paris dévalisé",
      no_hit_30s: "👻 Fantôme — 30s sans dégât",
    };
    if (!defs[id]) return;
    this.achievementsShown.add(id);
    // Toast en bas, juste au-dessus de la chip dist+combo
    this.addFloatingText(this.viewW() / 2, this.viewH() - 70, defs[id], "#ffd744", 1.4);
    this.playSfx("achievement", 0.2);
    tlrEvents.emit("tlr_game_achievement", { id });
  }

  /* ──────────────────────────────────────────
   * Render
   * ────────────────────────────────────────── */

  draw(now, gameSpeed) {
    const ctx = this.ctx;
    const w = this.viewW();
    const h = this.viewH();
    const city = CITIES[this.cityIdx];
    const layers = this.bgLayers?.[this.cityIdx];
    const isLayerReady = (img) => img && img.complete && img.naturalWidth > 0;

    // Si des plans sont CONFIGURÉS pour cette ville → ils ont priorité absolue
    // (on ignore sky/clouds/city/full même s'ils existent)
    const hasPlansConfigured = layers?.plans?.length > 0;
    const hasMultiLayer = !hasPlansConfigured && layers &&
      (isLayerReady(layers.sky) || isLayerReady(layers.clouds) || isLayerReady(layers.city));
    const hasFullLayer = !hasPlansConfigured && layers && isLayerReady(layers.full);

    if (hasPlansConfigured) {
      // ━ Multi-plans parallax (back → front) ━
      // Sky fallback gradient si aucun plan n'est encore chargé (évite flash noir)
      const anyPlanReady = layers.plans.some((p) => isLayerReady(p.img));
      if (!anyPlanReady) {
        const sky = ctx.createLinearGradient(0, 0, 0, h);
        sky.addColorStop(0, city.sky[0]);
        sky.addColorStop(1, city.sky[1]);
        ctx.fillStyle = sky;
        ctx.fillRect(0, 0, w, h);
      } else {
        // Les FRONT_PLANS_COUNT derniers (= arbres + sol) seront dessinés
        // APRÈS le perso pour créer une vraie profondeur 3D (le perso passe
        // derrière les arbres, le sol couvre tout). Voir drawFrontPlans().
        const FRONT_PLANS_COUNT = 2;
        const backCount = Math.max(0, layers.plans.length - FRONT_PLANS_COUNT);
        const isParis = this.cityIdx === 0;
        const dayProg = isParis ? Math.min(1, this.distance / 7500) : 0;

        for (let i = 0; i < backCount; i++) {
          const plan = layers.plans[i];
          if (isLayerReady(plan.img)) {
            this.drawTiledLayer(ctx, w, h, plan.img, plan.speed);
          }
        }
        // Voile sombre subtle pour faire ressortir les sprites
        ctx.fillStyle = "rgba(0, 0, 0, 0.12)";
        ctx.fillRect(0, 0, w, h);
      }
    } else if (hasMultiLayer) {
      // ━ Multi-layer parallax (sky → clouds → city) ━
      // Sky : très lent (presque immobile)
      if (isLayerReady(layers.sky)) {
        this.drawTiledLayer(ctx, w, h, layers.sky, 0.08);
      } else {
        // fallback sky gradient
        const sky = ctx.createLinearGradient(0, 0, 0, h);
        sky.addColorStop(0, city.sky[0]);
        sky.addColorStop(1, city.sky[1]);
        ctx.fillStyle = sky;
        ctx.fillRect(0, 0, w, h);
      }
      // Clouds : medium parallax
      if (isLayerReady(layers.clouds)) {
        this.drawTiledLayer(ctx, w, h, layers.clouds, 0.25);
      }
      // City : fast parallax (au plus proche)
      if (isLayerReady(layers.city)) {
        this.drawTiledLayer(ctx, w, h, layers.city, 0.6);
      }
      // Voile sombre pour faire ressortir les sprites
      ctx.fillStyle = "rgba(0, 0, 0, 0.15)";
      ctx.fillRect(0, 0, w, h);
    } else if (hasFullLayer) {
      // ━ Single panorama tilé ━
      this.drawTiledLayer(ctx, w, h, layers.full, 0.5);
      ctx.fillStyle = "rgba(0, 0, 0, 0.18)";
      ctx.fillRect(0, 0, w, h);
    } else if (this.cityIdx === 4) {
      // ━ Overdrive : hazard stripes jaune/noir en diagonale (run final) ━
      this.drawHazardStripes(ctx, w, h);
    } else {
      // ━ Fallback procédural ━
      // Sky gradient
      const sky = ctx.createLinearGradient(0, 0, 0, h);
      sky.addColorStop(0, city.sky[0]);
      sky.addColorStop(1, city.sky[1]);
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, w, h);
      if (this.cityIdx === 3) this.drawNeons(ctx, w, h, now);
      this.drawBuildings(ctx, w, h, this.bgOffset * 0.3, 0.55, city.building, city.roof);
      this.drawBuildings(ctx, w, h, this.bgOffset * 0.7, 0.7, this.shade(city.building, -0.15), this.shade(city.roof, -0.15));
      // Ground
      ctx.fillStyle = city.ground;
      ctx.fillRect(0, h - 50, w, 50);
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      for (let i = 0; i < w + 80; i += 80) {
        const x = ((i - this.bgOffset) % (w + 80) + (w + 80)) % (w + 80) - 80;
        ctx.fillRect(x, h - 30, 40, 4);
      }
    }

    // ━ Smoke (behind player) ━
    this.smoke.forEach((s) => {
      ctx.fillStyle = `rgba(255,255,255,${Math.min(0.7, s.life / 32)})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
      ctx.fill();
    });

    // ━ Pochons ━
    this.pochons.forEach((o) => this.drawPochon(ctx, o, now));
    // ━ Stars ━
    this.stars.forEach((o) => this.drawStar(ctx, o, now));
    this.hearts.forEach((o) => this.drawHeart(ctx, o, now));
    this.powerups.forEach((o) => this.drawPowerup(ctx, o, now));
    // ━ Cops ━
    this.lasers.forEach((o) => this.drawLaser(ctx, o, now));
    this.cops.forEach((o) => this.drawCop(ctx, o, COP_COLORS[this.cityIdx]));
    this.drones.forEach((o) => this.drawDrone(ctx, o, now));
    if (this.boss) this.drawBoss(ctx, this.boss, now);
    this.bullets.forEach((b) => this.drawBullet(ctx, b));

    // ━ Player ━
    this.drawPlayer(ctx, this.player, now);

    // ━ Front plans (arbres + sol) après le perso pour vraie profondeur 3D ━
    if (hasPlansConfigured) {
      const FRONT_PLANS_COUNT = 2;
      const start = Math.max(0, layers.plans.length - FRONT_PLANS_COUNT);
      for (let i = start; i < layers.plans.length; i++) {
        const plan = layers.plans[i];
        if (isLayerReady(plan.img)) {
          this.drawTiledLayer(ctx, w, h, plan.img, plan.speed);
        }
      }
    }

    // ━ Boost étoile actif : aura dorée rainbow autour du perso (mode Mario star) ━
    if (now < this.boostUntil) {
      ctx.save();
      const cx = this.player.x + this.player.w / 2;
      const cy = this.player.y + this.player.h / 2;
      const pulse = Math.sin(now / 80) * 0.15 + 1;
      const r = this.player.w * 1.0 * pulse;
      // Cycle de couleurs rapide (jaune → orange → rose → violet → cyan → vert)
      const hue = (now / 4) % 360;
      const aura = ctx.createRadialGradient(cx, cy, r * 0.3, cx, cy, r);
      aura.addColorStop(0, `hsla(${hue}, 100%, 70%, 0.0)`);
      aura.addColorStop(0.5, `hsla(${hue}, 100%, 60%, 0.5)`);
      aura.addColorStop(1, `hsla(${hue}, 100%, 50%, 0.7)`);
      ctx.fillStyle = aura;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // ━ Bouclier actif : halo bleu autour du perso ━
    if (now < this.shieldUntil) {
      ctx.save();
      const pulse = Math.sin(now / 150) * 0.15 + 1;
      const cx = this.player.x + this.player.w / 2;
      const cy = this.player.y + this.player.h / 2;
      const r = this.player.w * 0.9 * pulse;
      const grad = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, r);
      grad.addColorStop(0, "rgba(6, 182, 212, 0)");
      grad.addColorStop(0.7, "rgba(6, 182, 212, 0.35)");
      grad.addColorStop(1, "rgba(6, 182, 212, 0.6)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // ━ Flash rouge sur damage (320ms, alpha max 0.7 — partout dans le jeu) ━
    if (now < this.flashUntil) {
      const alpha = (this.flashUntil - now) / 320 * 0.7;
      ctx.fillStyle = `rgba(255, 45, 45, ${alpha})`;
      ctx.fillRect(0, 0, w, h);
    }

    // ━ Slow-mo overlay (vignette violette) ━
    if (now < this.slowmoUntil) {
      ctx.save();
      const vignette = ctx.createRadialGradient(w / 2, h / 2, w * 0.3, w / 2, h / 2, w * 0.7);
      vignette.addColorStop(0, "rgba(123, 63, 228, 0)");
      vignette.addColorStop(1, "rgba(123, 63, 228, 0.3)");
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    // ━ Floating texts (au-dessus de tout sauf overlays UI) ━
    this.drawFloatingTexts(ctx);

    // ━ Boost overlay ━
    if (now < this.boostUntil) {
      ctx.fillStyle = "rgba(216, 255, 90, 0.08)";
      ctx.fillRect(0, 0, w, h);
      // Speed lines
      ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
      ctx.lineWidth = 2;
      for (let i = 0; i < 14; i++) {
        const ly = (Math.random() * h);
        const lx = ((Math.random() * w * 2 + this.bgOffset * 4) % (w + 100)) - 50;
        ctx.beginPath();
        ctx.moveTo(lx, ly);
        ctx.lineTo(lx + 60, ly);
        ctx.stroke();
      }
    }
  }

  drawPlayer(ctx, p, now) {
    const blink = now < this.invincibleUntil && Math.floor(now / 100) % 2 === 0;
    if (blink) return;

    // Selon l'état du joueur, on choisit le sprite Tealer adapté :
    //   - boost (fumée max) si étoile collectée récemment
    //   - thrust (fumée moyenne) si maintien appuyé
    //   - idle (petite fumée) en chute libre
    // Si un sprite manque, on fallback sur le suivant (thrust > idle > boost).
    let spriteName = "tealerIdle";
    if (now < this.boostUntil)        spriteName = "tealerBoost";
    else if (this.thrusting)          spriteName = "tealerThrust";
    const sprite = this.getSprite(spriteName, "tealerThrust") || this.getSprite("tealerThrust", "tealerIdle");

    if (sprite) {
      ctx.save();
      ctx.translate(p.x + p.w / 2, p.y + p.h / 2);
      const tilt = Math.max(-0.4, Math.min(0.4, p.vy * 0.04));
      ctx.rotate(tilt);
      // Sprite Tealer : maintien aspect ratio du PNG, scaled pour faire la hauteur du player
      const ratio = sprite.naturalWidth / sprite.naturalHeight;
      const drawH = p.h * 1.8;          // un peu plus haut que la hitbox pour la fumée
      const drawW = drawH * ratio;
      ctx.drawImage(sprite, -drawW * 0.5, -drawH * 0.5, drawW, drawH);
      ctx.restore();
      return;
    }

    ctx.save();
    ctx.translate(p.x + p.w / 2, p.y + p.h / 2);
    const tilt = Math.max(-0.4, Math.min(0.4, p.vy * 0.04));
    ctx.rotate(tilt);

    // Jetpack derrière (rect gris)
    ctx.fillStyle = "#3a3a3a";
    this.roundRect(ctx, -p.w * 0.42, -p.h * 0.1, p.w * 0.3, p.h * 0.55, 6);
    ctx.fill();
    // Flamme jetpack
    if (this.thrusting) {
      const grad = ctx.createLinearGradient(-p.w * 0.42, p.h * 0.45, -p.w * 0.42, p.h * 0.85);
      grad.addColorStop(0, "#ffd744");
      grad.addColorStop(0.5, "#ff6a13");
      grad.addColorStop(1, "rgba(255, 106, 19, 0)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(-p.w * 0.42, p.h * 0.45);
      ctx.lineTo(-p.w * 0.18, p.h * 0.45);
      ctx.lineTo(-p.w * 0.3 + Math.sin(now / 50) * 4, p.h * 0.9);
      ctx.closePath();
      ctx.fill();
    }

    // Corps Tealer (vert)
    ctx.fillStyle = "#1f7a3a";
    this.roundRect(ctx, -p.w * 0.18, -p.h * 0.05, p.w * 0.42, p.h * 0.5, 8);
    ctx.fill();

    // Tête (peau)
    ctx.fillStyle = "#f4c2a1";
    ctx.beginPath();
    ctx.arc(p.w * 0.05, -p.h * 0.18, p.w * 0.18, 0, Math.PI * 2);
    ctx.fill();

    // Casquette TEALER (bleu marine)
    ctx.fillStyle = "#1e3a8a";
    this.roundRect(ctx, -p.w * 0.16, -p.h * 0.42, p.w * 0.42, p.h * 0.18, 4);
    ctx.fill();
    // Visière casquette
    ctx.fillStyle = "#1e3a8a";
    this.roundRect(ctx, -p.w * 0.05, -p.h * 0.28, p.w * 0.28, p.h * 0.06, 3);
    ctx.fill();
    // Texte "T" sur casquette
    ctx.fillStyle = "#fff";
    ctx.font = `bold ${Math.floor(p.w * 0.13)}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("T", p.w * 0.05, -p.h * 0.34);

    // Joint au coin de la bouche
    ctx.fillStyle = "#fffaeb";
    ctx.fillRect(p.w * 0.18, -p.h * 0.12, p.w * 0.12, 3);
    ctx.fillStyle = "#ff6a13";
    ctx.fillRect(p.w * 0.28, -p.h * 0.12, 3, 3);
    // Fumée joint
    ctx.fillStyle = "rgba(220, 220, 220, 0.5)";
    ctx.beginPath();
    ctx.arc(p.w * 0.34, -p.h * 0.18, 4, 0, Math.PI * 2);
    ctx.arc(p.w * 0.4, -p.h * 0.24, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  drawCop(ctx, c, color) {
    const wobble = Math.sin(c.bob) * 4;

    const cityKey = ["paris", "berlin", "moscow", "hk"][this.cityIdx] || "paris";
    const spriteName = "cop" + this.cap(cityKey);
    const sprite = this.getSprite(spriteName, "copParis");
    if (sprite) {
      ctx.save();
      ctx.translate(c.x + c.w / 2, c.y + c.h / 2 + wobble);
      // Maintien aspect ratio + slight tilt to give a chasing vibe
      const ratio = sprite.naturalWidth / sprite.naturalHeight;
      const drawH = c.h * 1.6;
      const drawW = drawH * ratio;
      ctx.drawImage(sprite, -drawW * 0.5, -drawH * 0.5, drawW, drawH);
      ctx.restore();
      return;
    }

    ctx.save();
    ctx.translate(c.x + c.w / 2, c.y + c.h / 2 + wobble);

    // Corps (uniforme bleu)
    ctx.fillStyle = color;
    this.roundRect(ctx, -c.w * 0.28, -c.h * 0.12, c.w * 0.56, c.h * 0.55, 6);
    ctx.fill();

    // Tête
    ctx.fillStyle = "#f4c2a1";
    ctx.beginPath();
    ctx.arc(0, -c.h * 0.22, c.w * 0.2, 0, Math.PI * 2);
    ctx.fill();

    // Casquette flic
    ctx.fillStyle = color;
    this.roundRect(ctx, -c.w * 0.22, -c.h * 0.48, c.w * 0.44, c.h * 0.12, 3);
    ctx.fill();
    // Visière
    this.roundRect(ctx, -c.w * 0.26, -c.h * 0.36, c.w * 0.5, c.h * 0.06, 2);
    ctx.fill();
    // Badge (carré jaune)
    ctx.fillStyle = "#ffd744";
    this.roundRect(ctx, -c.w * 0.05, -c.h * 0.42, c.w * 0.1, c.h * 0.08, 2);
    ctx.fill();

    // Yeux (méchants)
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(-c.w * 0.1, -c.h * 0.24, 4, 5);
    ctx.fillRect(c.w * 0.06, -c.h * 0.24, 4, 5);

    ctx.restore();
  }

  drawPochon(ctx, p, now = 0) {
    const sprite = this.getSprite("pochon");
    if (sprite) {
      ctx.save();
      // Bobbing vertical doux : oscille de ±3px sur 1.6s
      const bob = Math.sin(now / 250 + p.x * 0.01) * 3;
      // Rotation continue lente : 1 tour toutes les 4s
      const rot = (now / 4000) * Math.PI * 2 + p.x * 0.001;
      // Pulse léger : scale entre 0.95 et 1.05 sur 1.2s
      const pulse = 1 + Math.sin(now / 200 + p.x * 0.02) * 0.05;

      ctx.translate(p.x + p.w / 2, p.y + p.h / 2 + bob);
      ctx.rotate(Math.sin(rot) * 0.25);
      ctx.scale(pulse, pulse);

      // Halo vert lime subtil derrière (glow)
      ctx.fillStyle = "rgba(43, 212, 122, 0.18)";
      ctx.beginPath();
      ctx.arc(0, 0, p.w * 0.65 + Math.sin(now / 300) * 3, 0, Math.PI * 2);
      ctx.fill();

      // Sprite pochon maintenant aspect ratio
      const ratio = sprite.naturalWidth / sprite.naturalHeight;
      const drawH = p.h * 1.4; // un peu plus grand que la hitbox
      const drawW = drawH * ratio;
      ctx.drawImage(sprite, -drawW * 0.5, -drawH * 0.5, drawW, drawH);
      ctx.restore();
      return;
    }

    ctx.save();
    ctx.translate(p.x + p.w / 2, p.y + p.h / 2);
    ctx.rotate(Math.sin(p.spin) * 0.15);

    // Sac zip (transparent + bord rouge)
    ctx.fillStyle = "rgba(220, 230, 240, 0.4)";
    this.roundRect(ctx, -p.w * 0.45, -p.h * 0.45, p.w * 0.9, p.h * 0.9, 6);
    ctx.fill();
    ctx.strokeStyle = "#dc2626";
    ctx.lineWidth = 2;
    this.roundRect(ctx, -p.w * 0.45, -p.h * 0.45, p.w * 0.9, p.h * 0.9, 6);
    ctx.stroke();
    // Zip horizontal
    ctx.strokeStyle = "rgba(0,0,0,0.3)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(-p.w * 0.4, -p.h * 0.32);
    ctx.lineTo(p.w * 0.4, -p.h * 0.32);
    ctx.stroke();

    // Feuille de cannabis (forme étoile à 5 branches stylisée)
    ctx.fillStyle = "#16a34a";
    ctx.strokeStyle = "#0a0a0a";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const leaves = 5;
    const r = p.w * 0.32;
    const r2 = p.w * 0.12;
    for (let i = 0; i < leaves * 2; i++) {
      const angle = (i / (leaves * 2)) * Math.PI * 2 - Math.PI / 2;
      const radius = i % 2 === 0 ? r : r2;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.restore();
  }

  drawStar(ctx, s, now) {
    ctx.save();
    ctx.translate(s.x + s.w / 2, s.y + s.h / 2);
    ctx.rotate(s.spin);
    // Halo
    ctx.fillStyle = "rgba(255, 215, 68, 0.4)";
    ctx.beginPath();
    ctx.arc(0, 0, s.w * 0.6 + Math.sin(now / 200) * 4, 0, Math.PI * 2);
    ctx.fill();
    // Étoile 5 branches
    ctx.fillStyle = "#ffd744";
    ctx.strokeStyle = "#92400e";
    ctx.lineWidth = 2;
    ctx.beginPath();
    const points = 5;
    const r = s.w * 0.45;
    const r2 = s.w * 0.2;
    for (let i = 0; i < points * 2; i++) {
      const angle = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
      const radius = i % 2 === 0 ? r : r2;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Dessine un cœur bonus ❤️ pulsant avec halo rouge/rose.
   * Sprite-based si dispo, sinon procédural (path SVG-like).
   */
  drawHeart(ctx, h, now) {
    ctx.save();
    ctx.translate(h.x + h.w / 2, h.y + h.h / 2);
    const pulse = 1 + Math.sin(now / 300) * 0.08;
    ctx.scale(pulse, pulse);
    ctx.rotate(Math.sin(h.spin) * 0.1);

    // Halo rose/rouge pulsant
    ctx.fillStyle = "rgba(255, 95, 168, 0.35)";
    ctx.beginPath();
    ctx.arc(0, 0, h.w * 0.7 + Math.sin(now / 250) * 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(255, 45, 45, 0.25)";
    ctx.beginPath();
    ctx.arc(0, 0, h.w * 0.55, 0, Math.PI * 2);
    ctx.fill();

    // Forme cœur (path classique)
    const s = h.w * 0.4;
    ctx.fillStyle = "#ff2d4d";
    ctx.strokeStyle = "#7a0820";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(0, s * 0.85);
    ctx.bezierCurveTo(-s * 1.4, -s * 0.2, -s * 0.7, -s * 1.1, 0, -s * 0.3);
    ctx.bezierCurveTo(s * 0.7, -s * 1.1, s * 1.4, -s * 0.2, 0, s * 0.85);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Reflet blanc en haut-gauche pour effet 3D
    ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
    ctx.beginPath();
    ctx.ellipse(-s * 0.35, -s * 0.45, s * 0.18, s * 0.28, -0.4, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  /**
   * Dessine un power-up avec icône emoji + halo coloré selon le type.
   */
  drawPowerup(ctx, p, now) {
    const cfg = {
      shield: { emoji: "🛡", glow: "rgba(6, 182, 212, 0.6)" },
      magnet: { emoji: "🧲", glow: "rgba(255, 95, 168, 0.6)" },
      double: { emoji: "💎", glow: "rgba(216, 255, 90, 0.6)" },
      slowmo: { emoji: "🐢", glow: "rgba(123, 63, 228, 0.6)" },
    }[p.kind] || { emoji: "?", glow: "rgba(255,255,255,0.4)" };
    ctx.save();
    const bob = Math.sin(now / 300 + p.x * 0.01) * 4;
    ctx.translate(p.x + p.w / 2, p.y + p.h / 2 + bob);
    ctx.rotate(Math.sin(p.spin) * 0.12);
    // Halo
    const haloR = p.w * 0.7 + Math.sin(now / 250) * 5;
    ctx.fillStyle = cfg.glow;
    ctx.beginPath();
    ctx.arc(0, 0, haloR, 0, Math.PI * 2);
    ctx.fill();
    // Cercle blanc semi-transparent (effet "bulle")
    ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
    ctx.strokeStyle = "rgba(11, 11, 11, 0.4)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(0, 0, p.w * 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // Emoji centré
    ctx.font = `${Math.floor(p.w * 0.6)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(cfg.emoji, 0, 2);
    ctx.restore();
  }

  /**
   * Dessine un drone : rotor + corps noir avec œil rouge.
   */
  drawDrone(ctx, d, now) {
    const wobble = Math.sin(d.bob) * 3;
    ctx.save();
    ctx.translate(d.x + d.w / 2, d.y + d.h / 2 + wobble);
    // Rotor (cercle flou tournant)
    ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
    ctx.lineWidth = 2;
    const rotorR = d.w * 0.5;
    ctx.save();
    ctx.rotate(now / 30);
    ctx.beginPath();
    ctx.ellipse(0, -d.h * 0.5, rotorR, rotorR * 0.15, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    // Corps drone
    ctx.fillStyle = "#0a0a14";
    ctx.strokeStyle = "#ff2d2d";
    ctx.lineWidth = 2;
    this.roundRect(ctx, -d.w * 0.3, -d.h * 0.25, d.w * 0.6, d.h * 0.5, 8);
    ctx.fill();
    ctx.stroke();
    // Œil rouge LED
    const glow = 0.6 + Math.sin(now / 200) * 0.4;
    ctx.fillStyle = `rgba(255, 45, 45, ${glow})`;
    ctx.beginPath();
    ctx.arc(0, 0, d.w * 0.12, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(0, 0, d.w * 0.05, 0, Math.PI * 2);
    ctx.fill();
    // Pieds
    ctx.strokeStyle = "#444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-d.w * 0.25, d.h * 0.25);
    ctx.lineTo(-d.w * 0.35, d.h * 0.45);
    ctx.moveTo(d.w * 0.25, d.h * 0.25);
    ctx.lineTo(d.w * 0.35, d.h * 0.45);
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Dessine un laser horizontal pulsant rouge.
   */
  drawLaser(ctx, l, now) {
    const pulse = 0.6 + Math.sin(l.pulse) * 0.4;
    ctx.save();
    // Halo rouge
    ctx.fillStyle = `rgba(255, 45, 45, ${0.3 * pulse})`;
    ctx.fillRect(l.x - 10, l.y - 8, l.w + 20, l.h + 16);
    // Ligne principale
    const grad = ctx.createLinearGradient(l.x, l.y, l.x, l.y + l.h);
    grad.addColorStop(0, `rgba(255, 200, 200, ${pulse})`);
    grad.addColorStop(0.5, `rgba(255, 45, 45, ${pulse})`);
    grad.addColorStop(1, `rgba(255, 200, 200, ${pulse})`);
    ctx.fillStyle = grad;
    ctx.fillRect(l.x, l.y, l.w, l.h);
    // Émetteurs aux 2 bouts
    ctx.fillStyle = "#1a1a1a";
    ctx.strokeStyle = "#ff2d2d";
    ctx.lineWidth = 2;
    this.roundRect(ctx, l.x - 14, l.y - 8, 14, l.h + 16, 3);
    ctx.fill();
    ctx.stroke();
    this.roundRect(ctx, l.x + l.w, l.y - 8, 14, l.h + 16, 3);
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }

  /**
   * Dessine le boss : sprite custom par ville (Paris/Berlin/Moscou/Tokyo).
   * Halo rouge + bobbing déjà géré par tick(), ici juste le sprite.
   */
  drawBoss(ctx, b, now) {
    const spriteKey = `boss${this.cap(b.cityKey || "paris")}`;
    const img = this.sprites?.[spriteKey];

    ctx.save();
    ctx.translate(b.x, b.y);

    // Halo rouge pulsant pour signaler le danger
    const pulse = (Math.sin(now / 200) + 1) / 2; // 0→1
    ctx.fillStyle = `rgba(255, 45, 45, ${0.15 + pulse * 0.2})`;
    ctx.beginPath();
    ctx.ellipse(0, 0, b.w * 0.65, b.h * 0.65, 0, 0, Math.PI * 2);
    ctx.fill();

    // Léger bobbing vertical (le sprite bouge sans rotation pour rester lisible)
    const bob = Math.sin(now / 280) * 4;

    if (img && img.complete && img.naturalWidth > 0) {
      // Drawimage centré sur (0,0)
      ctx.drawImage(img, -b.w / 2, -b.h / 2 + bob, b.w, b.h);
    } else {
      // Fallback : carré rouge stylisé si sprite pas chargé
      ctx.fillStyle = "#1e3a8a";
      ctx.strokeStyle = "#ff2d2d";
      ctx.lineWidth = 3;
      ctx.fillRect(-b.w / 2, -b.h / 2 + bob, b.w, b.h);
      ctx.strokeRect(-b.w / 2, -b.h / 2 + bob, b.w, b.h);
      ctx.fillStyle = "#fff";
      ctx.font = `bold ${Math.floor(b.w * 0.12)}px sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText("BOSS", 0, 6);
    }

    // Phare avant rouge clignotant (indépendant du sprite, donne du feedback "actif")
    const flash = Math.sin(now / 150) > 0;
    ctx.fillStyle = flash ? "#ff2d2d" : "#7a0820";
    ctx.beginPath();
    ctx.arc(b.w * 0.38, 0 + bob, 5, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  /**
   * Ciel 100% procédural pour Paris — remplace complètement l'image sky.
   * 4 keyframes de gradient vertical, interpolées en continu sur dayProg 0→1 :
   *   - 0%   matin doré (jaune→bleu clair)
   *   - 33%  midi bleu clair (cyan→bleu)
   *   - 66%  coucher rose-orange-violet
   *   - 100% nuit deep blue + étoiles + lune
   * Le soleil dessiné en jour, la lune en nuit.
   */
  drawProceduralSky(ctx, w, h, dayProg, now) {
    const p = Math.max(0, Math.min(1, dayProg));
    const lerp = (a, b, t) => a + (b - a) * t;
    const lerpColor = (c1, c2, t) => [
      Math.round(lerp(c1[0], c2[0], t)),
      Math.round(lerp(c1[1], c2[1], t)),
      Math.round(lerp(c1[2], c2[2], t)),
    ];
    const rgb = (c) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

    // Keyframes pour le gradient vertical (3 stops : top, middle, horizon)
    // Chaque keyframe = [topColor, middleColor, horizonColor]
    const KF = [
      // 0% Matin : ciel doré clair en haut, bleu pâle au milieu, doré-rose à l'horizon
      [[255, 200, 130], [255, 220, 180], [255, 180, 120]],
      // 33% Midi : bleu profond en haut, bleu ciel au milieu, bleu pâle à l'horizon
      [[60, 130, 220], [120, 180, 240], [200, 220, 240]],
      // 66% Coucher : violet profond en haut, rose-orange au milieu, jaune-rouge à l'horizon
      [[80, 30, 110], [220, 90, 130], [255, 140, 60]],
      // 100% Nuit : noir-bleu en haut, bleu nuit au milieu, bleu sombre à l'horizon
      [[2, 8, 30], [10, 24, 60], [25, 40, 85]],
    ];

    // Détermine la phase et le facteur d'interpolation
    let kfA, kfB, t;
    if (p < 0.33)      { kfA = KF[0]; kfB = KF[1]; t = p / 0.33; }
    else if (p < 0.66) { kfA = KF[1]; kfB = KF[2]; t = (p - 0.33) / 0.33; }
    else               { kfA = KF[2]; kfB = KF[3]; t = (p - 0.66) / 0.34; }

    const topC = lerpColor(kfA[0], kfB[0], t);
    const midC = lerpColor(kfA[1], kfB[1], t);
    const horC = lerpColor(kfA[2], kfB[2], t);

    // Gradient principal du ciel
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, rgb(topC));
    grad.addColorStop(0.5, rgb(midC));
    grad.addColorStop(1, rgb(horC));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    const sunAlpha = p < 0.75 ? 1 - Math.max(0, (p - 0.65) / 0.1) : 0;
    if (sunAlpha > 0.01) {
      // Position du soleil : monte au matin, redescend au coucher (arc)
      // p 0 → x=0.15, y=0.5 (lever); p 0.5 → x=0.5, y=0.18 (midi); p 1 → x=0.85, y=0.5 (couché)
      const sunX = lerp(0.15, 0.85, p);
      const sunY = 0.18 + Math.sin((1 - Math.abs(p - 0.5) * 2) * Math.PI / 2) * (-0.05); // arc
      const sunRadius = Math.min(38, h * 0.075);
      // Couleur soleil : jaune au matin/midi, orange-rouge au coucher
      const sunColor = p < 0.5
        ? [255, 240, 150]  // jaune chaud
        : lerpColor([255, 240, 150], [255, 110, 60], (p - 0.5) / 0.3);
      ctx.save();
      ctx.globalAlpha = sunAlpha;
      // Halo large
      const halo = ctx.createRadialGradient(sunX * w, sunY * h, 0, sunX * w, sunY * h, sunRadius * 4);
      halo.addColorStop(0, `rgba(${sunColor[0]}, ${sunColor[1]}, ${sunColor[2]}, 0.4)`);
      halo.addColorStop(0.4, `rgba(${sunColor[0]}, ${sunColor[1]}, ${sunColor[2]}, 0.15)`);
      halo.addColorStop(1, `rgba(${sunColor[0]}, ${sunColor[1]}, ${sunColor[2]}, 0)`);
      ctx.fillStyle = halo;
      ctx.fillRect(sunX * w - sunRadius * 4, sunY * h - sunRadius * 4, sunRadius * 8, sunRadius * 8);
      // Disque
      ctx.fillStyle = `rgb(${sunColor[0]}, ${sunColor[1]}, ${sunColor[2]})`;
      ctx.beginPath();
      ctx.arc(sunX * w, sunY * h, sunRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    const nightAlpha = Math.max(0, Math.min(1, (p - 0.7) / 0.3));
    if (nightAlpha > 0.01) {
      ctx.save();
      ctx.globalAlpha = nightAlpha;
      // Étoiles seedées (positions stables, scintillement individuel)
      for (let i = 0; i < 60; i++) {
        const sx = ((i * 9301 + 49297) % 233280) / 233280 * w;
        const sy = ((i * 18247 + 1) % 100000) / 100000 * h * 0.6; // zone ciel (top 60%)
        const twinkle = 0.5 + 0.5 * Math.abs(Math.sin(now / 700 + i * 1.7));
        ctx.fillStyle = `rgba(255, 255, 230, ${twinkle.toFixed(3)})`;
        const size = 1 + (i % 4) * 0.5;
        ctx.fillRect(sx, sy, size, size);
        // 1 étoile sur 9 = brillante avec rayonnement diffus
        if (i % 9 === 0) {
          ctx.fillStyle = `rgba(255, 255, 230, ${(twinkle * 0.25).toFixed(3)})`;
          ctx.fillRect(sx - 2, sy - 2, size + 4, size + 4);
        }
      }
      // Lune (apparaît à partir de 80%)
      if (p > 0.8) {
        const moonAlpha = Math.min(1, (p - 0.8) / 0.15);
        ctx.globalAlpha = nightAlpha * moonAlpha;
        const mx = w * 0.82, my = h * 0.18, mr = Math.min(40, h * 0.075);
        // Halo doux
        const halo = ctx.createRadialGradient(mx, my, mr * 0.4, mx, my, mr * 3.5);
        halo.addColorStop(0, "rgba(255, 250, 230, 0.5)");
        halo.addColorStop(0.5, "rgba(255, 250, 230, 0.15)");
        halo.addColorStop(1, "rgba(255, 250, 230, 0)");
        ctx.fillStyle = halo;
        ctx.fillRect(mx - mr * 3.5, my - mr * 3.5, mr * 7, mr * 7);
        // Disque lunaire
        ctx.fillStyle = "#f7f3e0";
        ctx.beginPath();
        ctx.arc(mx, my, mr, 0, Math.PI * 2);
        ctx.fill();
        // Petits cratères
        ctx.fillStyle = "rgba(170, 165, 145, 0.45)";
        ctx.beginPath();
        ctx.arc(mx + mr * 0.3, my - mr * 0.25, mr * 0.15, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(mx - mr * 0.25, my + mr * 0.2, mr * 0.1, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(mx + mr * 0.1, my + mr * 0.35, mr * 0.07, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }

  /**
   * (Legacy stub conservé pour compat — non utilisé maintenant que drawProceduralSky existe.)
   */
  drawSkyTint(ctx, w, h, dayProg) {
    const p = dayProg;
    const lerp = (a, b, t) => a + (b - a) * t;
    const lerpRGBA = (a, b, t) => [
      lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t), lerp(a[3], b[3], t),
    ];
    const K = [
      [255, 215, 160, 0.15], // matin doré
      [255, 255, 255, 0.00], // midi
      [255, 100, 40,  0.22], // coucher
      [255, 100, 40,  0.22], // garde le coucher max jusqu'au fade out
    ];
    let color;
    if (p < 0.33)      color = lerpRGBA(K[0], K[1], p / 0.33);
    else if (p < 0.66) color = lerpRGBA(K[1], K[2], (p - 0.33) / 0.33);
    else               color = K[2];
    if (color[3] > 0.01) {
      ctx.fillStyle = `rgba(${color[0]|0}, ${color[1]|0}, ${color[2]|0}, ${color[3].toFixed(3)})`;
      ctx.fillRect(0, 0, w, h);
    }
  }

  /**
   * Dessine un VRAI ciel nocturne procédural :
   *  - gradient deep blue → black du haut vers le bas
   *  - étoiles scintillantes
   *  - lune avec halo
   * Apparaît progressivement quand l'image sky est fade out (dayProg 0.5 → 0.75).
   * Une fois dayProg > 0.75, c'est full procédural.
   */
  drawNightSky(ctx, w, h, dayProg, now) {
    // Alpha fade-in : 0 à dayProg=0.5, 1 à dayProg=0.75
    const fadeIn = Math.min(1, Math.max(0, (dayProg - 0.5) / 0.25));
    if (fadeIn <= 0) return;
    ctx.save();
    ctx.globalAlpha = fadeIn;

    // Gradient vertical : haut = bleu nuit profond, bas = bleu-noir
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, "#020c2e");      // top noir-bleu très profond
    grad.addColorStop(0.4, "#0a1840");    // bleu nuit
    grad.addColorStop(0.7, "#1a2858");    // bleu plus clair vers l'horizon
    grad.addColorStop(1, "#2a3068");      // horizon nuit lointaine
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    // Étoiles scintillantes (zone ciel = top 55%)
    for (let i = 0; i < 55; i++) {
      const sx = ((i * 9301 + 49297) % 233280) / 233280 * w;
      const sy = ((i * 18247 + 1) % 100000) / 100000 * h * 0.55;
      const twinkle = 0.45 + 0.55 * Math.abs(Math.sin(now / 600 + i * 1.3));
      ctx.fillStyle = `rgba(255, 255, 230, ${twinkle.toFixed(3)})`;
      const size = 1 + (i % 4) * 0.6;
      ctx.fillRect(sx, sy, size, size);
      // Quelques étoiles plus brillantes avec lueur
      if (i % 11 === 0) {
        ctx.fillStyle = `rgba(255, 255, 230, ${(twinkle * 0.3).toFixed(3)})`;
        ctx.fillRect(sx - 1, sy - 1, size + 2, size + 2);
      }
    }

    // Lune en haut-droite (apparaît surtout en pleine nuit)
    if (dayProg > 0.7) {
      const moonAlpha = Math.min(1, (dayProg - 0.7) / 0.2);
      ctx.globalAlpha = fadeIn * moonAlpha;
      const mx = w * 0.85, my = h * 0.18, mr = Math.min(36, h * 0.07);
      // Halo
      const halo = ctx.createRadialGradient(mx, my, mr * 0.5, mx, my, mr * 3);
      halo.addColorStop(0, "rgba(255, 250, 230, 0.4)");
      halo.addColorStop(1, "rgba(255, 250, 230, 0)");
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(mx, my, mr * 3, 0, Math.PI * 2);
      ctx.fill();
      // Disque lunaire
      ctx.fillStyle = "#f4f1e0";
      ctx.beginPath();
      ctx.arc(mx, my, mr, 0, Math.PI * 2);
      ctx.fill();
      // Cratères subtils
      ctx.fillStyle = "rgba(180, 175, 155, 0.45)";
      ctx.beginPath();
      ctx.arc(mx + mr * 0.3, my - mr * 0.2, mr * 0.15, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(mx - mr * 0.2, my + mr * 0.25, mr * 0.1, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Dessine un projectile laser rouge avec trail.
   */
  drawBullet(ctx, b) {
    // Trail derrière le projectile (effet vitesse)
    const trailLen = 18;
    const tx = b.x - b.vx * 1.8;
    const ty = b.y - b.vy * 1.8;
    const grad = ctx.createLinearGradient(tx, ty, b.x, b.y);
    grad.addColorStop(0, "rgba(255, 45, 45, 0)");
    grad.addColorStop(1, "rgba(255, 80, 80, 0.85)");
    ctx.strokeStyle = grad;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    // Tête du projectile (halo + cœur blanc)
    ctx.fillStyle = "rgba(255, 80, 80, 0.5)";
    ctx.beginPath();
    ctx.arc(b.x, b.y, b.r * 1.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#fff";
    ctx.beginPath();
    ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Dessine tous les floating texts (au-dessus des sprites, sous les overlays).
   */
  drawFloatingTexts(ctx) {
    if (!this.floatingTexts.length) return;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    for (const t of this.floatingTexts) {
      const alpha = Math.min(1, t.life / 20);
      const fontSize = Math.floor(18 * (t.scale || 1));
      ctx.font = `bold ${fontSize}px "Bebas Neue", "Anton", system-ui, sans-serif`;
      ctx.globalAlpha = alpha;
      // Ombre noire pour lisibilité
      ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
      ctx.fillText(t.text, t.x + 2, t.y + 2);
      // Texte coloré
      ctx.fillStyle = t.color;
      ctx.fillText(t.text, t.x, t.y);
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  /**
   * Tile une image horizontalement avec parallax paramétrable.
   * @param parallaxSpeed - 0 = immobile, 1 = même vitesse que le jeu, 0.3 = slow, 0.6 = fast
   * L'image est scaled pour faire la hauteur du canvas, puis tilée à l'infini.
   */
  drawTiledLayer(ctx, w, h, img, parallaxSpeed = 0.5) {
    // Utilise le canvas avec black-keyed transparency si dispo
    const source = img._processed || img;
    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;
    const scale = h / naturalH;
    const tileW = naturalW * scale;
    if (tileW <= 0) return;
    const offset = ((this.bgOffset * parallaxSpeed) % tileW + tileW) % tileW;
    let x = -offset;
    let guard = 0; // garde-fou anti-freeze (au cas où w/tileW serait délirant)
    while (x < w && guard++ < 2000) {
      ctx.drawImage(source, x, 0, tileW, h);
      x += tileW;
    }
  }

  drawBuildings(ctx, w, h, offset, scale, color, roofColor) {
    const bandHeight = h * 0.45;
    const baseY = h - 50 - bandHeight * scale;
    const bw = 90;
    const cycle = bw + 10;
    for (let i = -1; i < Math.ceil(w / cycle) + 2; i++) {
      const x = i * cycle - (offset % cycle);
      const seed = Math.abs(Math.sin(i * 12.9898 + this.cityIdx * 7) * 43758.5453);
      const localH = (bandHeight * scale) * (0.5 + (seed % 1) * 0.5);
      ctx.fillStyle = color;
      ctx.fillRect(x, h - 50 - localH, bw, localH);
      // Toit
      ctx.fillStyle = roofColor;
      ctx.fillRect(x - 2, h - 50 - localH, bw + 4, 8);
      // Fenêtres
      ctx.fillStyle = this.cityIdx === 3 ? "rgba(216, 255, 90, 0.5)" : "rgba(255, 215, 68, 0.18)";
      const cols = 3;
      const rows = Math.floor(localH / 22);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const wx = x + 14 + c * 22;
          const wy = h - 50 - localH + 14 + r * 22;
          if (wx > x + bw - 10) break;
          if (wy > h - 60) break;
          ctx.fillRect(wx, wy, 10, 10);
        }
      }
    }
  }

  /**
   * Countdown overlay 3·2·1·GO! avant le start du gameplay.
   * Affiche un voile sombre + le numéro courant en gros lime + le rappel des contrôles.
   * Chaque step dure 1s, "GO!" dure 0.5s à la fin (total = 3500ms).
   */
  drawCountdown(ctx, w, h, now) {
    const remaining = this.countdownEndsAt - now;
    if (remaining <= 0) return;

    // Voile sombre semi-transparent
    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    ctx.fillRect(0, 0, w, h);

    // Détermine le step actuel
    let label;
    let stepProgress; // 0 → 1 dans le step en cours
    if (remaining > 2500) {
      label = "3";
      stepProgress = 1 - (remaining - 2500) / 1000;
    } else if (remaining > 1500) {
      label = "2";
      stepProgress = 1 - (remaining - 1500) / 1000;
    } else if (remaining > 500) {
      label = "1";
      stepProgress = 1 - (remaining - 500) / 1000;
    } else {
      label = "GO!";
      stepProgress = 1 - remaining / 500;
    }

    // Animation : scale du gros au petit + fade out à la fin
    const scale = label === "GO!"
      ? 1 + stepProgress * 1.5         // GO grossit
      : 2.4 - stepProgress * 1.4;      // 3-2-1 rétrécit (2.4 → 1.0)
    const alpha = label === "GO!"
      ? 1 - Math.pow(stepProgress, 2)  // GO fade out
      : Math.min(1, stepProgress * 3); // 3-2-1 fade in rapide

    // Couleur : GO orange, numbers lime
    const color = label === "GO!" ? "#ff6a13" : "#d8ff5a";
    const shadowColor = label === "GO!" ? "rgba(255, 106, 19, 0.6)" : "rgba(216, 255, 90, 0.6)";

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(w / 2, h * 0.45);
    ctx.scale(scale, scale);

    // Texte avec shadow / glow
    const fontSize = Math.min(w, h) * 0.22;
    ctx.font = `bold ${fontSize}px "Bebas Neue", "Anton", system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    // Glow
    ctx.shadowColor = shadowColor;
    ctx.shadowBlur = 40;
    ctx.fillStyle = color;
    ctx.fillText(label, 0, 0);
    // Stroke noir
    ctx.shadowBlur = 0;
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#0b0b0b";
    ctx.strokeText(label, 0, 0);
    ctx.fillText(label, 0, 0);

    ctx.restore();

    ctx.save();
    ctx.globalAlpha = Math.min(1, (this.countdownDuration - remaining) / 500);
    const ctrlFontSize = Math.max(12, h * 0.025);
    const padY = h * 0.85;
    ctx.font = `600 ${ctrlFontSize}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
    ctx.shadowColor = "rgba(0, 0, 0, 0.6)";
    ctx.shadowBlur = 8;
    ctx.fillText("MAINTIENS POUR VOLER", w / 2, padY);

    // Boutons d'action (3 pills côte à côte)
    const pillFontSize = Math.max(10, h * 0.02);
    ctx.font = `700 ${pillFontSize}px system-ui, sans-serif`;
    const items = ["👆 TAP", "⎵ ESPACE", "↑ FLÈCHE"];
    const gap = w * 0.025;
    const pillsY = padY + h * 0.045;
    // Mesure largeur totale
    const widths = items.map((s) => ctx.measureText(s).width + pillFontSize * 1.8);
    const totalW = widths.reduce((a, b) => a + b, 0) + gap * (items.length - 1);
    let x = w / 2 - totalW / 2;
    items.forEach((s, i) => {
      const pw = widths[i];
      const ph = pillFontSize * 1.9;
      ctx.shadowBlur = 0;
      ctx.fillStyle = "rgba(255, 255, 255, 0.12)";
      this.roundRect(ctx, x, pillsY - ph / 2, pw, ph, ph / 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#fff";
      ctx.fillText(s, x + pw / 2, pillsY);
      x += pw + gap;
    });
    ctx.restore();
  }

  /** Rayures hazard diagonales (jaune sur noir) qui défilent — fond Overdrive.
   *  Glitch : pendant le flash de damage, on déchire l'écran en bandes horizontales
   *  décalées aléatoirement (effet VHS rouge/jaune) pour amplifier l'impact. */
  drawHazardStripes(ctx, w, h) {
    const now = performance.now();
    const isGlitching = now < this.flashUntil;

    // Fond noir
    ctx.fillStyle = "#0a0a0a";
    ctx.fillRect(0, 0, w, h);
    // Bandes jaunes diagonales (~-45°), scroll avec bgOffset
    const stripeW   = Math.max(50, Math.floor(w * 0.08));
    const stripeGap = stripeW;
    const pitch     = stripeW + stripeGap;
    const baseOffset = (this.bgOffset * 0.35) % pitch;
    const diag      = Math.sqrt(w * w + h * h) * 1.4;
    // Glitch : décalage random qui change toutes les 50ms (sub-frame "scanlines")
    const glitchOffset = isGlitching
      ? Math.floor(Math.sin(now / 50) * 60) + (Math.random() - 0.5) * 30
      : 0;
    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(-Math.PI / 4);
    ctx.translate(-diag / 2, -diag / 2);
    ctx.fillStyle = "#ffd700";
    for (let x = -baseOffset + glitchOffset; x < diag; x += pitch) {
      ctx.fillRect(x, 0, stripeW, diag);
    }
    ctx.restore();
    // Glitch : 3 bandes horizontales rouges décalées (effet déchirure VHS) pendant le flash
    if (isGlitching) {
      ctx.fillStyle = "rgba(255, 45, 45, 0.35)";
      const tearH = 12;
      for (let i = 0; i < 3; i++) {
        const y = (Math.random() * h) | 0;
        ctx.fillRect(0, y, w, tearH);
      }
    }
    // Voile sombre subtle pour faire ressortir le sprite joueur
    ctx.fillStyle = "rgba(0, 0, 0, 0.25)";
    ctx.fillRect(0, 0, w, h);
  }

  drawNeons(ctx, w, h, now) {
    // Random neon dots for HK skyline
    const seed = (i) => Math.abs(Math.sin(i * 999.123 + 5) * 1234.5) % 1;
    for (let i = 0; i < 40; i++) {
      const x = (seed(i) * w + this.bgOffset * 0.1) % w;
      const y = seed(i + 100) * h * 0.6;
      const flick = Math.sin(now / 200 + i) > 0 ? 1 : 0.4;
      const colors = ["#ff5fa8", "#7b3fe4", "#d8ff5a", "#06b6d4"];
      ctx.fillStyle = colors[i % 4];
      ctx.globalAlpha = 0.5 * flick;
      ctx.fillRect(x, y, 2, 2);
    }
    ctx.globalAlpha = 1;
  }

  roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  shade(hex, percent) {
    const m = hex.match(/\w\w/g);
    if (!m) return hex;
    const [r, g, b] = m.map((x) => parseInt(x, 16));
    const adj = (c) => Math.max(0, Math.min(255, Math.round(c + 255 * percent)));
    return `rgb(${adj(r)}, ${adj(g)}, ${adj(b)})`;
  }

  /* ──────────────────────────────────────────
   * End of game
   * ────────────────────────────────────────── */

  /**
   * Desktop safety net at the end of a run: the stage is a block in the page, not a
   * fullscreen surface, and the page may have scrolled during the run. The gameover
   * card holds the reward, so the view comes back to it — but only when the stage is
   * mostly off screen, to never yank a player who can already see it.
   */
  keepStageInView() {
    const stage = this.closest(".tlr-game-arena__stage");
    if (!stage || stage.classList.contains("tlr-game-arena__stage--fullscreen-mobile")) return;
    if (this.isStageInView()) return;
    stage.scrollIntoView({ behavior: this.reducedMotion ? "auto" : "smooth", block: "center" });
  }

  /**
   * Is the arena on screen? Half of it showing counts — it is the threshold for both
   * callers: below it the end of a run brings the stage back, and above it the gameover
   * card is entitled to keep the thrust keys. Fullscreen and a stage that cannot be
   * measured (display:none, no layout yet) both count as on screen: there is nothing to
   * scroll, and the keys belong to a game that fills the screen.
   */
  isStageInView() {
    const stage = this.closest(".tlr-game-arena__stage");
    if (!stage) return false;
    if (stage.classList.contains("tlr-game-arena__stage--fullscreen-mobile")) return true;
    const rect = stage.getBoundingClientRect();
    const viewH = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!rect.height || !viewH) return true;
    const visible = Math.min(rect.bottom, viewH) - Math.max(rect.top, 0);
    return visible >= Math.min(rect.height, viewH) * 0.5;
  }

  endGame() {
    if (this.gameLoop) cancelAnimationFrame(this.gameLoop);
    this.gameLoop = null;
    this.thrusting = false;
    this._pausedAt = 0;
    this._pendingStartToken = 0;
    this._rotateBlocked = false;
    this.stopMusic();

    const score = this.score;
    const cityIdx = this.cityIdx;
    const durationMs = Math.max(0, Math.round(performance.now() - this.gameStartTime));
    const isNewBest = score > this.bestScore;
    if (isNewBest) {
      this.bestScore = score;
      this.session.bestScore = score; // local copy, demo stats only
      this.saveSession();
    }

    this.updateBestScore();

    this.state = STATE.GAMEOVER;
    this.applyState();
    // Written AFTER the card is revealed: a live region hidden at write time is silent.
    if (this.refs.finalScore) this.refs.finalScore.textContent = String(score);

    // The run ends where it was played: mobile stays fullscreen (the card is laid out
    // for a landscape stage) and the desktop view comes back to the stage if it drifted.
    // Leaving is the player's move — the ✕, a claim, or a pack purchase.
    this.keepStageInView();

    tlrEvents.emit("tlr_game_ended", {
      customer_id: this.customerId,
      demo: this.demoMode,
      score,
      distance: Math.floor(this.distance),
      city_idx: cityIdx,
      duration_ms: durationMs,
      is_new_best: isNewBest,
    });

    // Demo (guest, theme editor, API down): nothing was at stake, nothing is claimed.
    const runId = this.runId;
    if (this.demoMode || !runId) {
      this.renderRewardsList(null);
      return;
    }

    this.runId = null; // exactly one /run/finish per run — the route is idempotent
    const token = this._runToken;
    const payload = { runId, score, cityIdx, durationMs };
    this._lastFinishPayload = payload; // kept for the "try again" button
    this.renderRewardPending();
    this.finishRun(payload, token).then((res) => this.applyFinishResult(res, token));
  }

  /**
   * Single place where a /run/finish answer becomes screen content. A run the server
   * refuses (404 run_not_found, 422 implausible) gets a neutral message — never a
   * reward, never an accusation.
   */
  applyFinishResult(res, token) {
    if (token !== this._runToken) return; // the player already relaunched
    if (res.status === "invalid")     { this.renderRewardsNotice(this.t("runInvalid")); return; }
    if (res.status === "rateLimited") { this.renderRewardsNotice(this.t("rateLimited")); return; }
    if (res.status !== "ok") {
      this.markRewardsOffline();
      this.renderRewardsList(null);
      return;
    }
    const data = res.data;
    if (Number.isFinite(data.credits)) {
      this.credits = data.credits;
      this.updateCreditsDisplay();
    }
    if (Number.isFinite(data.bestScore)) {
      this.bestScore = data.bestScore;
      this.updateBestScore();
    }
    const reward = data.reward && typeof data.reward === "object" ? data.reward : null;
    if (!reward && data.capped === true) {
      this.renderRewardsNotice(this.t("rewardCapped"));
      return;
    }
    this.renderRewardsList(reward);
  }

  /**
   * The server owed a code and sent none: the route is idempotent, so the button just
   * asks again with the same runId instead of leaving the player with a dead card.
   */
  async retryFinishRun(button) {
    const payload = this._lastFinishPayload;
    if (!payload) return;
    if (button) {
      button.disabled = true;
      button.textContent = this.t("ctaWorking");
    }
    const token = this._runToken;
    const res = await this.finishRun(payload, token);
    this.applyFinishResult(res, token);
  }

  /** Waiting state while /run/finish is in flight — never a reward, never a code. */
  renderRewardPending() {
    this.renderRewardsNotice(this.t("rewardPending"));
  }

  /** One-line message in the gameover card (waiting, refused run, daily cap, outage). */
  renderRewardsNotice(text) {
    const list = this.refs.rewardsList;
    if (!list) return;
    const notice = document.createElement("p");
    notice.className = "tlr-game__rewards-empty";
    notice.textContent = text;
    list.replaceChildren(notice);
  }

  /**
   * The gameover card shows exactly what /run/finish returned, and nothing else:
   * no score tier, no city rule, no local decision. `null` = nothing won.
   */
  renderRewardsList(reward) {
    const list = this.refs.rewardsList;
    if (!list) return;
    if (!reward || typeof reward !== "object") {
      this.renderRewardsNotice(this.rewardsOffline ? this.t("rewardsOffline") : this.t("rewardEmpty"));
      return;
    }
    list.replaceChildren();

    const item = document.createElement("div");
    item.className = "tlr-game__reward-item";

    const head = document.createElement("div");
    head.className = "tlr-game__reward-head";
    const icon = document.createElement("span");
    icon.className = "tlr-game__reward-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "🎁";
    const meta = document.createElement("div");
    meta.className = "tlr-game__reward-meta";
    const label = document.createElement("strong");
    label.className = "tlr-game__reward-title";
    // Server copy: textContent, never innerHTML.
    label.textContent = reward.label || this.rewardFallbackLabel(reward.type);
    meta.appendChild(label);
    head.appendChild(icon);
    head.appendChild(meta);
    item.appendChild(head);

    // Credits are already on the account when the server answers: nothing to claim.
    if (reward.type !== "credits") {
      const hasCode = typeof reward.code === "string" && reward.code.trim() !== "";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "tlr-game__reward-btn";
      if (hasCode) {
        btn.textContent = this.rewardCtaLabel(reward.type);
        btn.addEventListener("click", () => this.claimReward(reward, btn));
        item.appendChild(btn);
      } else if (this._lastFinishPayload) {
        // Reward granted, code not issued yet: ask the idempotent route again.
        btn.textContent = this.t("ctaRetry");
        btn.addEventListener("click", () => this.retryFinishRun(btn));
        item.appendChild(btn);
      }
    }

    list.appendChild(item);

    tlrEvents.emit("tlr_game_prize_won", {
      customer_id: this.customerId,
      score: this.score,
      city_idx: this.cityIdx,
      prize_type: reward.type || null,
      prize_value: Number.isFinite(reward.value) ? reward.value : null,
    });
  }

  /** Label used when the server sends a reward without its own wording. */
  rewardFallbackLabel(type) {
    if (type === "credits") return this.t("rewardCredits");
    if (type === "discount_freebie") return this.t("rewardFreebie");
    return this.t("rewardDiscount");
  }

  rewardCtaLabel(type) {
    if (type === "discount_freebie") return this.t("ctaFreebie");
    return this.t("ctaDiscount");
  }

  /** Nothing reaches the cart before this click. */
  claimReward(reward, button) {
    if (!reward || reward._claimed) return;
    // The claim ends in the cart drawer, and fullscreen hides every fixed element on
    // the page — the drawer would open behind the black stage. Claiming is the player
    // saying they are done playing, so this is where the run gives the page back.
    this.exitFullscreenMobile();
    if (reward.type === "discount_freebie") this.claimFreebieReward(reward, button);
    else this.claimDiscountReward(reward, button);
  }

  /** Non-blocking on-screen message. No failing path on the reward flow stays silent. */
  showToast(title, body = "") {
    const toast = document.createElement("div");
    toast.className = "tlr-game__reward-error-toast";
    toast.setAttribute("role", "alert");
    // The game's own UI: it lives in <body> and is position:fixed, exactly what the
    // fullscreen hides. Without this it would silence its own reward failure message.
    toast.setAttribute("data-tlr-game-ui", "");
    const strong = document.createElement("strong");
    strong.textContent = title;
    toast.appendChild(strong);
    if (body) {
      const span = document.createElement("span");
      span.textContent = body;
      toast.appendChild(span);
    }
    document.body.appendChild(toast);
    setTimeout(() => { toast.style.opacity = "0"; }, 5000);
    setTimeout(() => toast.remove(), 6000);
  }

  /**
   * Visible failure path for a reward that cannot be applied: a dead code in the
   * drawer betrays the player at checkout, so we say it instead of hiding it.
   */
  showRewardError(reward) {
    this.showToast(this.t("rewardErrorTitle"), this.t("rewardErrorBody"));
    tlrEvents.emit("tlr_game_reward_failed", {
      customer_id: this.customerId,
      reward_type: reward?.type || "unknown",
      reason: "claim-failed",
    });
  }

  // RÈGLE ANTI-CUMUL + ANTI-ZOMBIES : un seul code Tealer Run actif à la fois,
  // ET on vire les codes inapplicables (applicable:false) qui squattent les slots
  // de classe Shopify (max 1 code par classe order/product/shipping au checkout).
  //
  // Sans le strip des zombies, si l'user a TLRPACK15 (class PRODUCT) du panier
  // précédent mais sans assez d'items, ce code "vide" bloque le slot product →
  // notre TRLRUNOVR (class PRODUCT, -100% pochon) est rejeté par Shopify.
  //
  // Mécanique : Shopify n'a pas d'endpoint pour retirer un code spécifique →
  // POST /cart/update.js {discount:""} clear TOUT, puis ré-apply uniquement les
  // codes qui contribuaient vraiment (applicable:true) ET non-Tealer Run.
  async _stripOldTealerRunCodes() {
    const isTealerRun = (c) => /^(TRLRUN|TEALERRUN)/i.test(c || "");
    try {
      const cartRes = await this.cartRequest("/cart.js");
      const cart = cartRes.data || {};
      const existing = (cart.discount_codes || []).filter(
        (d) => d && typeof d.code === "string" && d.code.length > 0,
      );
      const hasTealerRun = existing.some((d) => isTealerRun(d.code));
      const hasZombies = existing.some((d) => !d.applicable && !isTealerRun(d.code));
      if (!hasTealerRun && !hasZombies) return; // tout propre

      await this.cartRequest("/cart/update.js", { method: "POST", body: { discount: "" } });
      // Ré-applique uniquement les codes ACTIVEMENT applicables ET non-Tealer Run
      const keep = existing
        .filter((d) => d.applicable && !isTealerRun(d.code))
        .map((d) => d.code);
      for (const c of keep) {
        await this.cartRequest(`/discount/${encodeURIComponent(c)}`);
      }
    } catch (_) { /* best-effort */ }
  }

  /**
   * Applique un code promo en background (/discount/CODE pose le cookie session Shopify),
   * rafraîchit le drawer et l'ouvre — sans navigation pleine page.
   * Strip d'abord les anciens codes Tealer Run pour éviter le cumul.
   */
  async _applyCodeAndOpenDrawer(code) {
    if (!code) return;
    try {
      await this._stripOldTealerRunCodes();
      await this.cartRequest(`/discount/${encodeURIComponent(code)}`);
    } catch (_) { /* ignore */ }
    document.dispatchEvent(new CustomEvent("cart:updated"));
    if (typeof window.tlrOpenCart === "function") {
      window.tlrOpenCart();
    } else {
      window.location.href = `/cart`;
    }
  }

  /** Applies the discount code the server issued for this run, then opens the drawer. */
  async claimDiscountReward(reward, button) {
    const code = typeof reward.code === "string" ? reward.code.trim() : "";
    if (!code) {
      this.showRewardError(reward);
      return;
    }
    reward._claimed = true;
    if (button) {
      button.disabled = true;
      button.textContent = this.t("ctaWorking");
    }
    tlrEvents.emit("tlr_game_reward_claimed", {
      customer_id: this.customerId,
      reward_type: "discount",
      value: Number.isFinite(reward.value) ? reward.value : null,
    });
    await this._applyCodeAndOpenDrawer(code);
    if (button) button.textContent = this.t("ctaDone");
  }

  /**
   * Overdrive reward — 100 % native Shopify, both codes issued by the server:
   *   - `code`        : the order discount (Basic)
   *   - `freebieCode` : −100 % on the gift variant (Bxgy)
   * The gift is added to the cart here, then both codes are applied one by one
   * (`/discount/A,B` is read by Shopify as a single code "A,B" that does not exist).
   */
  async claimFreebieReward(reward, button) {
    const orderCode = typeof reward.code === "string" ? reward.code.trim() : "";
    const freebieCode = typeof reward.freebieCode === "string" ? reward.freebieCode.trim() : "";
    if (!orderCode && !freebieCode) {
      this.showRewardError(reward);
      return;
    }
    reward._claimed = true;
    if (button) {
      button.disabled = true;
      button.textContent = this.t("ctaWorking");
    }

    tlrEvents.emit("tlr_game_reward_claimed", {
      customer_id: this.customerId,
      reward_type: "discount_freebie",
      value: Number.isFinite(reward.value) ? reward.value : null,
    });

    // Anti-cumul : on retire d'abord les anciens codes Tealer Run du panier.
    await this._stripOldTealerRunCodes();

    // Variant id du cadeau : le product picker Liquid renvoie null sur cet env, donc
    // on passe par le handle + /products/HANDLE.js (titre de variante configurable).
    let freebieVariantId = null;
    const handle = (this.dataset.freebieHandle || "").trim();
    if (freebieCode && handle) {
      try {
        const pRes = await this.cartRequest(`/products/${encodeURIComponent(handle)}.js`);
        if (pRes.ok && pRes.data) {
          const product = pRes.data;
          const target = (this.dataset.freebieVariantTitle || "").toLowerCase().replace(/\s/g, "");
          const match = target
            ? (product.variants || []).find((v) => (v.title || "").toLowerCase().replace(/\s/g, "") === target)
            : null;
          freebieVariantId = match?.id || (product.variants && product.variants[0]?.id) || null;
        }
      } catch (e) {
        console.warn("[TEALER RUN] freebie product fetch failed", e);
      }
    }

    if (freebieVariantId) {
      const addRes = await this.cartRequest("/cart/add.js", {
        method: "POST",
        body: {
          items: [{
            id: parseInt(freebieVariantId, 10),
            quantity: 1,
            properties: { "_tlr": "v2", "_tlr_game_freebie": "overdrive" },
          }],
        },
      });
      if (!addRes.ok) {
        console.warn("[TEALER RUN] cart/add.js freebie failed", addRes.status);
        this.showRewardError(reward);
      }
    } else if (freebieCode) {
      // Le code cadeau serait posé sur un panier sans le produit : on le dit.
      this.showRewardError(reward);
    }

    // Both codes via XHR too: the same fetch patch that hangs /cart/* could hang any
    // URL, and a hang here would freeze the claim after the pochon is already in.
    for (const c of [orderCode, freebieCode].filter(Boolean)) {
      await this.cartRequest(`/discount/${encodeURIComponent(c)}`);
    }

    document.dispatchEvent(new CustomEvent("cart:updated"));
    if (typeof window.tlrOpenCart === "function") {
      window.tlrOpenCart();
    } else {
      window.location.href = "/cart";
    }
    if (button) button.textContent = this.t("ctaDone");
  }

  /* ──────────────────────────────────────────
   * UI helpers
   * ────────────────────────────────────────── */

  updateHud() {
    // The score is aria-live="off" on purpose (it changes several times per second);
    // it is announced once on the gameover card. Writes are still guarded so the DOM
    // is not rewritten on every frame.
    if (this.refs.score && this._hudScore !== this.score) {
      this._hudScore = this.score;
      this.refs.score.textContent = String(this.score);
    }
    if (this.refs.distance) {
      const meters = Math.floor(this.distance);
      if (this._hudDistance !== meters) {
        this._hudDistance = meters;
        this.refs.distance.textContent = `${meters}m`;
      }
    }
    if (this.refs.flag)     this.refs.flag.textContent = this.flag;
    if (this.refs.cityName) {
      this.refs.cityName.textContent = (CITIES[this.cityIdx]?.name || CITIES[0].name).toUpperCase();
    }
    if (this.refs.combo && this.refs.comboWrap) {
      const mult = this.getComboMultiplier ? this.getComboMultiplier() : 1;
      if (this.combo >= 5 && mult > 1) {
        this.refs.comboWrap.removeAttribute("hidden");
        this.refs.combo.textContent = `×${mult}`;
      } else {
        this.refs.comboWrap.setAttribute("hidden", "");
      }
    }
    if (this.refs.lives) {
      // As many hearts as the run actually carries (3 by default, more once a bonus
      // heart is picked up) — never a fixed row of five for three lives.
      this._lifeSlots = Math.max(3, this._lifeSlots, this.lives);
      let dots = Array.from(this.refs.lives.querySelectorAll("span"));
      while (dots.length < this._lifeSlots) {
        const extra = document.createElement("span");
        extra.textContent = "❤";
        extra.classList.add("tlr-game__hud-life-bonus");
        this.refs.lives.appendChild(extra);
        dots.push(extra);
      }
      while (dots.length > this._lifeSlots) dots.pop().remove();
      dots.forEach((dot, i) => dot.classList.toggle("is-lost", i >= this.lives));
    }
  }

  updateBestScore() {
    if (this.refs.bestScore) {
      this.refs.bestScore.textContent = this.bestScore ? String(this.bestScore) : "—";
    }
  }

  updateCreditsDisplay() {
    if (this.refs.creditsValue) {
      // null = guest or unknown balance: a dash, never a number we made up.
      this.refs.creditsValue.textContent = Number.isFinite(this.credits) ? String(this.credits) : "—";
    }
  }

  /* ──────────────────────────────────────────
   * Share + checkout
   * ────────────────────────────────────────── */

  shareScore() {
    // %score% is the placeholder the section fills through the `| t` filter.
    const text = this.t("shareText").replace("%score%", String(this.score));
    // The arena can live on any page: share the page the player is on.
    const url = window.location.origin + window.location.pathname;
    if (navigator.share) {
      navigator.share({ title: "TEALER RUN", text, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(`${text} ${url}`);
      alert(this.t("shareCopied"));
    }
    tlrEvents.emit("tlr_game_share", { score: this.score });
  }

  openCheckout(pack) {
    // Credit packs are Shopify products; the credits are granted server-side by the
    // orders/paid webhook, never by a URL parameter on the way back.
    const ds = this.dataset || {};
    const handles = {
      starter: (ds.packStarterHandle || "").trim(),
      regular: (ds.packRegularHandle || "").trim(),
      addict:  (ds.packAddictHandle  || "").trim(),
    };
    const handle = handles[pack] || handles.regular;
    tlrEvents.emit("tlr_game_checkout_open", {
      customer_id: this.customerId,
      pack: pack || "default",
      product_handle: handle || null,
    });

    if (!handle) {
      alert(this.t("packsUnavailable"));
      return;
    }

    // No return_to: nothing reads it since the credits are granted by the orders/paid
    // webhook, and a dead parameter on a product URL is just noise in the analytics.
    window.location.href = `/products/${handle}`;
  }

  /**
   * No-credits pack card click: add the pack to the cart and go straight to checkout,
   * no detour through the product page. cartRequest (XHR) so the SlideCart fetch patch
   * cannot hang the add; on any failure or a missing variant, fall back to the card's
   * product URL so the click is never dead.
   */
  async buyPackAndCheckout(card) {
    tlrEvents.emit("tlr_game_buy_intent", { source: "no_credits", customer_id: this.customerId });
    const variantId = parseInt(card.dataset.variantId || "", 10);
    const productUrl = card.getAttribute("href");
    if (!variantId) { if (productUrl) window.location.href = productUrl; return; }
    const res = await this.cartRequest("/cart/add.js", {
      method: "POST",
      body: { items: [{ id: variantId, quantity: 1, properties: { _tlr: "v2" } }] },
    });
    window.location.href = res.ok ? "/checkout" : (productUrl || "/cart");
  }
}

if (!customElements.get("tlr-game")) {
  customElements.define("tlr-game", TlrGame);
}

export { TlrGame };
